import { FOOTER_TYPES, HEADER_TYPES } from 'src/consumer/constants'

// Measurement constants in inches (A4 page measurements)
// These will be converted to pixels based on DPI
// NOTE: Safety margins have been consolidated to avoid compounding
// The base margin in getRemainingPrescriptionBodyHeight is the ONLY safety margin applied
// Additional margins are only for specific item-type adjustments (e.g., text wrapping)
export const PRESCRIPTION_MEASUREMENTS_IN_INCHES = {
  // Base safety margin - applied ONCE in getRemainingPrescriptionBodyHeight
  // This is the ONLY safety margin between content and footer
  // Reduced further to allow better space utilization - items have their own CSS margins
  baseSafetyMarginWithFooter: 10 / 96, // ~0.104 inches (10px at 96 DPI) - REDUCED from 15px
  baseSafetyMarginWithoutFooter: 5 / 96, // ~0.052 inches (5px at 96 DPI) - REDUCED from 8px
  
  // Colored mode adds slightly more margin due to backgrounds, borders, etc.
  // NOTE: Reduced because items may already have CSS margins that provide spacing
  coloredModeExtraMargin: 2 / 96, // ~0.021 inches (2px at 96 DPI) - REDUCED from 5px to avoid overcounting
  
  // Drug advice table measurements (TABLE format only - not used for paragraphList)
  estimatedRowHeight: 55 / 96, // ~0.573 inches (55px at 96 DPI)
  headerRowHeight: 50 / 96, // ~0.521 inches (50px at 96 DPI)
  rowPadding: 3 / 96, // ~0.031 inches (3px at 96 DPI)
  chunkBottomPadding: 8 / 96, // ~0.083 inches (8px at 96 DPI) - REDUCED from 25px
  
  // Item spacing (between sections)
  // NOTE: This is minimal spacing - actual items may have CSS margins that provide additional spacing
  // We use a conservative value to avoid overcounting
  itemSpacing: 2 / 96, // ~0.021 inches (2px at 96 DPI) - REDUCED from 4px to account for CSS margins
  
  // Section spacing and titles
  sectionTitleHeight: 25 / 96, // ~0.260 inches (25px at 96 DPI)
  
  // Best-fit algorithm thresholds
  bestFitMinimumSpace: 40 / 96, // ~0.417 inches (40px at 96 DPI) - REDUCED from 60px for better utilization
  bestFitSafetyMargin: 20 / 96, // ~0.208 inches (20px at 96 DPI) - REDUCED from 50px
}

export const PRESCRIPTION_ITEM_ORDER_IDS = {
  continuedFrom: 'continuedFrom',
  continuedTo: 'continuedTo',
  patientDetails: 'patientDetails',
  prescriptionFooter: 'prescriptionFooter',
  assessments: 'psych_assessments',
  patientHistoryNotes: 'patient-history-notes',
  refer_to: 'refer_to',
  signature: 'signature',
}

export const EXCLUDED_ORDER_IDS_FROM_PRESC = [
  PRESCRIPTION_ITEM_ORDER_IDS.continuedTo,
  PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom,
  PRESCRIPTION_ITEM_ORDER_IDS.patientDetails,
  PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter,
]

export function getOrderItemsArrayWithTopLocation({ orderOfItems, headerHeight, footerHeight }) {
  const baseTopOffset = headerHeight
  let currentPosition = baseTopOffset
  let orderofItemsWithTopOffset = []

  for (let item of orderOfItems) {
    if (!item || !item?.dimension) {
      continue
    }
    let currentItem = { ...item }
    currentItem = { ...item, currentPosition }
    orderofItemsWithTopOffset = [...orderofItemsWithTopOffset, currentItem]

    currentPosition += item?.dimension?.height
  }
  return orderofItemsWithTopOffset
}

export function getPageDetailsNumberOfAnItem({
  headerHeight,
  footerHeight,
  pageHeight,
  pageBreaks = [],
  itemInfo,
}) {
  const currentTopPosition = itemInfo?.currentPosition
  const currentBottomPosition = itemInfo?.currentPosition + itemInfo?.dimension?.height
  const numberOfPages = pageBreaks?.length + 1
  const pagesStartToEndMapArr = Array.from({ length: numberOfPages }, (v, idx) => {
    return {
      start: pageHeight * idx,
      end: pageHeight * (idx + 1),
    }
  })

  let pages = []

  let idx = 0

  for (let pageDetails of pagesStartToEndMapArr) {
    if (currentTopPosition > pageDetails?.start && currentTopPosition < pageDetails?.end) {
      pages?.push(idx + 1)
    }
    if (currentBottomPosition > pageDetails?.start && currentBottomPosition < pageDetails?.end) {
      pages?.push(idx + 1)
    }
    ++idx
  }

  pages = new Set(pages)

  return Array.from(pages || [])
}

function getPageNumberBasedOnPrescriptionCurrentCoordinates({
  pageBreaks,
  yCoordinate,
  xCoordinate,
  pageHeight,
  currentBottomPosition: endingCoordinate,
}) {
  const currentBottomPosition = endingCoordinate || yCoordinate

  const numberOfPages = pageBreaks?.length + 1
  const pagesStartToEndMapArr = Array.from({ length: numberOfPages }, (v, idx) => {
    return {
      start: pageHeight * idx,
      end: pageHeight * (idx + 1),
    }
  })

  let pages = []

  let idx = 0
  for (let pageDetails of pagesStartToEndMapArr) {
    if (yCoordinate > pageDetails?.start && yCoordinate < pageDetails?.end) {
      pages?.push(idx + 1)
    }
    if (currentBottomPosition > pageDetails?.start && currentBottomPosition < pageDetails?.end) {
      pages?.push(idx + 1)
    }
    ++idx
  }
  pages = new Set(pages)

  return Array.from(pages || [])?.[0]
}

export function getCoordinatesStringForPrescItems({ item, a4PageDimensions, prescriptionBreaks }) {
  return `COORDINATES => TL: (${(item?.dimension?.x - a4PageDimensions?.x)?.toFixed(
    2,
  )}, ${item?.currentPosition?.toFixed(2)}, ${getPageNumberBasedOnPrescriptionCurrentCoordinates({
    xCoordinate: item?.dimension?.x - a4PageDimensions?.x,
    yCoordinate: item?.currentPosition,
    pageBreaks: prescriptionBreaks,
    pageHeight: a4PageDimensions?.height,
  })}); 
  TR: (${(item?.dimension?.x - a4PageDimensions?.x + item?.dimension?.width)?.toFixed(
    2,
  )}, ${item?.currentPosition?.toFixed(2)}, ${getPageNumberBasedOnPrescriptionCurrentCoordinates({
    xCoordinate: item?.dimension?.x - a4PageDimensions?.x + item?.dimension?.width,
    yCoordinate: item?.currentPosition,
    pageBreaks: prescriptionBreaks,
    pageHeight: a4PageDimensions?.height,
  })}) BL: (${(item?.dimension?.x - a4PageDimensions?.x)?.toFixed(2)}, ${(
    item?.currentPosition + item?.dimension?.height
  )?.toFixed(2)}, ${getPageNumberBasedOnPrescriptionCurrentCoordinates({
    xCoordinate: item?.dimension?.x - a4PageDimensions?.x,
    yCoordinate: item?.currentPosition + item?.dimension?.height,
    pageBreaks: prescriptionBreaks,
    pageHeight: a4PageDimensions?.height,
  })}) BR: (${(item?.dimension?.x - a4PageDimensions?.x + item?.dimension?.width)?.toFixed(2)}, ${(
    item?.currentPosition + item?.dimension?.height
  )?.toFixed(2)}, ${getPageNumberBasedOnPrescriptionCurrentCoordinates({
    xCoordinate: item?.dimension?.x - a4PageDimensions?.x + item?.dimension?.width,
    yCoordinate: item?.currentPosition + item?.dimension?.height,
    pageBreaks: prescriptionBreaks,
    pageHeight: a4PageDimensions?.height,
  })})`
}

export function getPrescriptionItemsPageDetailsBasedOnHeaderFooterType({
  orderOfItems,
  headerHeight,
  footerHeight,
  headerType,
  footerType,
  pageHeight,
  pageBreaks,
  continuedFromPageHeight,
  continuedToPageHeight,
}) {
  const orderOfItemsWithPageDetails = []

  let currIdx = 0
  let currPage = 1


  for (let item of orderOfItems) {
    let prescriptionBodyHeight = getPrescriptionBodyHeight({
      pageHeight,
      headerHeight: getHeaderHeightBasedOnTypeAndPageIdx({
        headerType,
        pageIdx: currIdx,
        headerHeightSetByUser: headerHeight,
      }),
    })

    if (currIdx === orderOfItems?.length - 1) {
      prescriptionBodyHeight = prescriptionBodyHeight - footerHeight
    }

    const currentPosition = item?.currentPosition - headerHeight
    const currentBottomPosition =
      currentPosition + item?.dimension?.height - prescriptionBodyHeight * (currPage - 1)
    if (currentBottomPosition > prescriptionBodyHeight) {
      currPage += 1
    }

    orderOfItemsWithPageDetails.push({
      ...item,
      page: currPage,
    })
  }

  return orderOfItemsWithPageDetails
}

/**
 * Split items that need chunking (like drug_advice and patient_history_notes) into multiple smaller items
 * This allows the page break system to properly distribute chunks across pages
 */
export function splitItemsIntoChunks(
  itemsInOrder,
  a4Height,
  headerHeight,
  footerHeight,
  drugsAdvice = null,
  patientHistoryNotes = null,
) {
  if (!itemsInOrder || !Array.isArray(itemsInOrder)) {
    return itemsInOrder
  }

  const result = []
  
  for (const item of itemsInOrder) {
    // DON'T pre-chunk drug_advice - let getPrescriptionItemRenderingList handle it dynamically
    // This allows for better page-by-page fitting and prevents creating unnecessary chunks
    if (item?.orderId === 'drug_advice' && drugsAdvice && Array.isArray(drugsAdvice)) {
      const filteredDrugsAdvice = drugsAdvice.filter((drugItem) => drugItem?.drug?.label)
      if (filteredDrugsAdvice.length === 0) {
        result.push(item)
        continue
      }

      // Guard against undefined a4Height
      if (!a4Height || a4Height <= 0 || isNaN(a4Height)) {
        result.push(item)
        continue
      }

      // Store the full drug list in the item for dynamic processing
      // Don't create chunks here - let the page-breaking algorithm handle it
      result.push({
        ...item,
        // Keep as 'drug_advice' - not a chunk yet
        orderId: 'drug_advice',
        // Store full drug list for dynamic chunking
        fullDrugsAdvice: filteredDrugsAdvice,
      })
    } 
    // Patient history notes - DON'T chunk
    else if (item?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes) {
      result.push(item)
    } 
    else {
      result.push(item)
    }
  }
  
  return result
}

/**
 * Best-fit algorithm helper: Finds smaller items that can fit before a large item
 * Returns the index of the next item that fits, or -1 if none found
 */
function findNextFittingItem(
  items,
  startIndex,
  availableHeight,
  minSafetyMargin,
  getItemHeight,
  placedItems,
  currentPageItems,
  skipItemId = null, // Item to skip (the one that didn't fit)
  itemSpacing = 5, // Default item spacing, should match MEASUREMENTS.itemSpacing from caller
) {
  
  // Look ahead from startIndex to find smaller items that fit
  for (let i = startIndex; i < items.length; i++) {
    const item = items[i]
    
    // Skip the item that didn't fit
    if (skipItemId && item?.orderId === skipItemId) {
      continue
    }
    
    // Skip already placed items
    const isChunkItem = item?.orderId?.startsWith('drug_advice_chunk_')
    const inPlacedItems = !isChunkItem && placedItems.has(item?.orderId)
    const inCurrentPage = !isChunkItem && currentPageItems.includes(item?.orderId)
    if (inPlacedItems || inCurrentPage) {
      continue
    }
    
    // Skip chunks if page already has one
    const isChunk = item?.orderId?.startsWith('drug_advice_chunk_')
    const hasChunkOnPage = currentPageItems.some(id => id?.startsWith('drug_advice_chunk_'))
    if (isChunk && hasChunkOnPage) {
      continue
    }
    
    // Get item height
    const itemHeight = getItemHeight(item)
    const totalItemHeight = itemHeight + itemSpacing + minSafetyMargin
    
    // Check if item fits
    if (totalItemHeight <= availableHeight) {
      return i // Return index of fitting item
    }
  }
  
  return -1 // No fitting item found
}

export function getPrescriptionItemRenderingList(
  itemsInOrder,
  headerHeight,
  footerHeight,
  leftSidebarWidth,
  rightSidebarWidth,
  a4Height,
  headerPolicy,
  footerPolicy,
  sidebarPolicy,
  continuedDivHeight,
  drugsAdvice = null, // Add drugsAdvice parameter for chunking
  patientHistoryNotes = null, // Add patientHistoryNotes parameter for chunking
  useBestFit = false, // Enable best-fit algorithm with look-ahead
  dpi = 96, // DPI for inch-to-pixel conversion (default 96 for web standard)
  colorMode = 'colored', // 'colored' or 'bw' - affects spacing calculations
  drugAdviceTableType = 'default', // Drug advice table format type (default, HP, list, paragraphList)
) {
  try {
  // Determine if we're in colored mode (more visual elements = slightly more margin)
  const isColoredMode = colorMode === 'colored'
  const coloredModeExtraMargin = isColoredMode 
    ? convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.coloredModeExtraMargin, dpi)
    : 0
  
  // Convert inch-based measurements to pixels based on DPI
  // SIMPLIFIED: No more compound safety margins - we use ONE consistent margin
  const MEASUREMENTS = {
    // Item spacing (minimal, actual rendering handles visual spacing)
    itemSpacing: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.itemSpacing, dpi) + coloredModeExtraMargin,
    
    // Drug advice TABLE format measurements (not used for paragraphList which uses actual dimensions)
    estimatedRowHeight: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.estimatedRowHeight, dpi),
    headerRowHeight: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.headerRowHeight, dpi),
    rowPadding: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.rowPadding, dpi),
    chunkBottomPadding: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.chunkBottomPadding, dpi),
    
    // Section title height
    sectionTitleHeight: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.sectionTitleHeight, dpi),
    // Best-fit algorithm (tighter margins for better space utilization)
    bestFitMinimumSpace: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.bestFitMinimumSpace, dpi),
    bestFitSafetyMargin: convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.bestFitSafetyMargin, dpi),
    
    // Extra margin for colored mode
    coloredModeExtraMargin,
  }
  
  // Detect if drug advice format is non-table (paragraphList or list) - these don't have table headers
  const isNonTableFormat = drugAdviceTableType === 'paragraphList' || drugAdviceTableType === 'list'
  
  // Split items into chunks before processing
  const itemsWithChunks = splitItemsIntoChunks(itemsInOrder, a4Height, headerHeight, footerHeight, drugsAdvice, patientHistoryNotes)
  
  // Filter out items without dimensions, BUT keep patient history notes and drug_advice even if dimensions not ready
  // Rich text content dimensions can take time to measure, but we still want to render the content
  // drug_advice will be processed dynamically, so we keep it even without dimensions
  let bodyItemsInOrder = itemsWithChunks?.filter((item) => {
    // Always keep patient history notes if we have the data
    if (item?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes && patientHistoryNotes && patientHistoryNotes.length > 0) {
      return true
    }
    // Always keep free-form rich text even if dimensions aren't ready - CSS handles flow
    if (item?.orderId === 'free_form_rich_text') {
      return true
    }
    // Always keep drug_advice - it will be processed dynamically
    if (item?.orderId === 'drug_advice' && item?.fullDrugsAdvice) {
      return true
    }
    // Always keep existing drug_advice chunks - they have calculated dimensions and chunkData
    if (item?.orderId?.startsWith('drug_advice_chunk_') && item?.chunkData) {
      return true
    }
    // Always keep existing free-form rich text chunks - they have pixel-based chunk data
    if (item?.orderId?.startsWith('free_form_rich_text_chunk_') && item?.freeFormChunkData) {
      return true
    }
    // For other items, require valid dimensions
    return !!item?.dimension
  })
  let finalOrderObjects = []

  let currPage = 1
  let totalHeightInPxAccomodatedByItems = 0
  
  // Track global row numbers across all pages for continuous numbering
  let globalRowCounter = 0
  // Track remaining drugs to process (for dynamic drug_advice processing)
  let remainingDrugsToProcess = null
  let currentDrugAdviceItem = null
  // Track the original position of drug_advice item to maintain order
  let originalDrugAdviceIndex = -1
  // Track which items have already been placed on pages to prevent duplication
  const placedItems = new Set()
  
  // Track remaining free-form rich text pixels for pixel-based windowed chunking
  let remainingFreeFormPx = null  // null = not started; number = remaining pixels
  let freeFormNextStartY = 0      // pixel offset for the next chunk's start (for CSS windowing)
  let freeFormTotalPx = 0         // total measured height of the full free-form content
  let freeFormChunkIdx = 0        // current chunk counter

  let currentOrderObject = getPageDetailsObject(
    headerPolicy,
    footerPolicy,
    sidebarPolicy,
    currPage,
    [],
  )

  // SEQUENTIAL ALGORITHM (with best-fit look-ahead when enabled)
  // When useBestFit is true, we use look-ahead optimization within the sequential flow
  // Use while loop for best-fit to allow dynamic reordering
  let itemIndex = 0
  // Track items that were just inserted by best-fit to prevent infinite loops
  const recentlyInsertedByBestFit = new Set()
  
  while (itemIndex < bodyItemsInOrder.length) {
    let bodyItem = bodyItemsInOrder[itemIndex]
    // Declare itemFits at the start of each loop iteration so it's available throughout
    let itemFits = false
    
    // Skip items that have already been placed on a previous page
    // This prevents duplication when moving to new pages
    const isChunkItem = bodyItem?.orderId?.startsWith('drug_advice_chunk_') || bodyItem?.orderId?.startsWith('free_form_rich_text_chunk_')
    
    // Check multiple ways to ensure item hasn't been placed:
    // 1. Check placedItems Set
    // 2. Check if item is in any previous page's items array
    // 3. Check if item is already in current page's items array
    const inPlacedItems = !isChunkItem && placedItems.has(bodyItem?.orderId)
    const inPreviousPage = !isChunkItem && finalOrderObjects.some(page => 
      Array.isArray(page?.items) && page.items.includes(bodyItem?.orderId)
    )
    const inCurrentPage = !isChunkItem && Array.isArray(currentOrderObject?.items) && 
                          currentOrderObject.items.includes(bodyItem?.orderId)
    
    if (inPlacedItems || inPreviousPage || inCurrentPage) {
      itemIndex++
      continue
    }
    // Debug: Log which item is being processed (disabled to reduce console noise)
    // Uncomment if debugging chunking issues:
    // if (bodyItem?.orderId?.startsWith('drug_advice_chunk_')) {
    //   console.log('[Processing Chunk]', {
    //     orderId: bodyItem.orderId,
    //     chunkIndex: bodyItem.chunkIndex,
    //     chunkDataLength: bodyItem.chunkData?.length,
    //     currentPage: currPage,
    //     hasDimension: !!bodyItem.dimension,
    //     dimensionHeight: bodyItem.dimension?.height,
    //     itemsOnCurrentPage: currentOrderObject?.items
    //   })
    // }
    
    let headerHeightOnPage = getHeaderHeightBasedOnPolicyAndPage(
      headerHeight,
      currPage,
      headerPolicy,
    )
    let footerHeightOnPage = getFooterHeightBasedOnPolicyAndPage(
      footerHeight,
      currPage,
      footerPolicy,
    )
    
    // CRITICAL FIX: For onLastPage and inLineAtEnd policies, the footer is added as a regular item
    // in the orderOfPrescriptionItems. We need to ALWAYS account for its height when it's upcoming
    // to prevent items from overflowing over the footer.
    if ((footerPolicy === FOOTER_TYPES.onLastPage || footerPolicy === FOOTER_TYPES.inLineAtEnd) && footerHeight > 0) {
      // Check if footer item is already on current page
      const footerOnCurrentPage = currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
      
      // Check if footer item exists in the items list and is upcoming
      const footerItem = bodyItemsInOrder.find(item => item?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
      
      if (footerItem) {
        const footerItemIndex = bodyItemsInOrder.indexOf(footerItem)
        const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
        const footerIsUpcoming = footerItemIndex > currentItemIndex // Note: > not >= to exclude footer itself
        
        // Always account for footer height if:
        // 1. Footer is already on current page, OR
        // 2. Footer is an upcoming item (conservative approach to prevent overflow)
        if (footerOnCurrentPage || footerIsUpcoming) {
          footerHeightOnPage = footerHeight
        }
      }
    }
    
    //sidebar height not needed as of now.

    // Check if current page has "Continued from" div
    // If signature is the last item, there won't be a "Continued from" div, so don't subtract continuedDivHeight from available space
    const hasContinuedFrom = currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom)
    const isSignatureItem = bodyItem?.orderId === 'next_visit_details' || bodyItem?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.signature
    const isLastItem = itemIndex >= bodyItemsInOrder.length - 1
    
    // For signature (last item), don't account for continuedDivHeight since there won't be a "Continued from" div
    // For other items on continuation pages, we need to account for continuedDivHeight
    // Adjust totalHeightInPxAccomodatedByItems: if there's a "Continued from" div and it's NOT the signature as last item, add continuedDivHeight
    const continuedDivHeightToAdd = (hasContinuedFrom && !(isSignatureItem && isLastItem) && continuedDivHeight) ? continuedDivHeight : 0
    const adjustedTotalHeight = (totalHeightInPxAccomodatedByItems || 0) + continuedDivHeightToAdd
    
    let remainingPrescriptionBodyHeight = getRemainingPrescriptionBodyHeight(
      a4Height,
      headerHeightOnPage,
      footerHeightOnPage,
      adjustedTotalHeight,
      dpi,
    )
    
    
    // Initialize currentItemHeight early - will be updated in dynamic processing if needed
    let currentItemHeight = getCurrentItemHeight(bodyItem)
    
    // MULTI-PASS ALGORITHM: Try multiple strategies in order
    // Pass 1: Try to fit as-is
    // Pass 2: Try best-fit reordering (if enabled and item doesn't fit)
    // Pass 3: Try chunking (for chunkable items like drug advice)
    // Pass 4: Move to next page (if nothing else works)
    
    // DYNAMIC DRUG ADVICE PROCESSING
    // If this is a drug_advice item (not a chunk), process it dynamically
    const isDrugAdviceItem = bodyItem?.orderId === 'drug_advice' && bodyItem?.fullDrugsAdvice
    const isDrugAdviceChunk = bodyItem?.orderId?.startsWith('drug_advice_chunk_')
    const isFreeFormChunk = bodyItem?.orderId?.startsWith('free_form_rich_text_chunk_')
    const isChunk = isDrugAdviceChunk || isFreeFormChunk
    const isPatientHistoryNotes = bodyItem?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes
    const isFreeFormRichText = bodyItem?.orderId === 'free_form_rich_text'
    
    // Pre-check: does the entire free-form content fit on the current page as-is?
    let freeFormFitsAsIs = false
    if (isFreeFormRichText) {
      const ffAdjustedRemaining = remainingPrescriptionBodyHeight - MEASUREMENTS.itemSpacing
      const ffTolerance = colorMode === 'bw' ? 20 : 30
      freeFormFitsAsIs = currentItemHeight <= (ffAdjustedRemaining + ffTolerance)
      if (!freeFormFitsAsIs) {
        // Initialize pixel-tracking for chunking
        freeFormTotalPx = currentItemHeight
        remainingFreeFormPx = currentItemHeight
        freeFormNextStartY = 0
        freeFormChunkIdx = 0
      }
    }
    
    // Flag to track if chunk was moved to next page due to small space
    let chunkMovedToNextPage = false
    // Flag to track if we should skip best-fit for this iteration (e.g., after chunking)
    let skipBestFitThisIteration = false
    
    // Track the original position of drug_advice item when we first encounter it
    if (isDrugAdviceItem && originalDrugAdviceIndex === -1) {
      originalDrugAdviceIndex = bodyItemsInOrder.indexOf(bodyItem)
    }
    
    // If we have remaining drugs from a previous page, use those instead
    const drugsToProcess = remainingDrugsToProcess || (isDrugAdviceItem ? bodyItem.fullDrugsAdvice : null)
    
    // MULTI-PASS: For drug advice, first check if full item fits as-is
    // This is Pass 1: Try to fit as-is
    let drugAdviceFitsAsIs = false
    if (drugsToProcess && drugsToProcess.length > 0 && isDrugAdviceItem && !isDrugAdviceChunk) {
      // CRITICAL FIX: Use actual measured dimension if available, otherwise use estimated calculation
      // The actual dimension is more accurate, especially for non-table formats like paragraphList
      const actualDimensionHeight = bodyItem?.dimension?.height
      const hasActualDimension = actualDimensionHeight && actualDimensionHeight > 0
      
      // Calculate estimated height as fallback
      // For non-table formats (paragraphList/list), exclude header height and use larger row estimate
      const effectiveHeaderHeight = isNonTableFormat ? 0 : MEASUREMENTS.headerRowHeight
      const effectiveRowHeight = isNonTableFormat 
        ? Math.max(MEASUREMENTS.estimatedRowHeight, 80) // ParagraphList rows are typically taller
        : MEASUREMENTS.estimatedRowHeight
      const rowPadding = MEASUREMENTS.rowPadding
      const chunkBottomPadding = MEASUREMENTS.chunkBottomPadding
      const estimatedDrugAdviceHeight = effectiveHeaderHeight + (drugsToProcess.length * (effectiveRowHeight + rowPadding)) + chunkBottomPadding
      
      // Use actual dimension if available, otherwise use estimated (for table format compatibility)
      const fullDrugAdviceHeight = hasActualDimension ? actualDimensionHeight : estimatedDrugAdviceHeight
      
      // SIMPLIFIED: No additional safety margin needed - getRemainingPrescriptionBodyHeight already includes base margin
      // Just add item spacing for visual separation from other elements
      drugAdviceFitsAsIs = fullDrugAdviceHeight <= (remainingPrescriptionBodyHeight - MEASUREMENTS.itemSpacing)
      
      // If it fits as-is, we can skip chunking and process it normally
      if (drugAdviceFitsAsIs) {
        // Update currentItemHeight to reflect full drug advice
        currentItemHeight = fullDrugAdviceHeight
        // Process normally - will be placed below if it fits
      }
    }
    
    // MULTI-PASS: Pass 3 - Try chunking (only if item doesn't fit as-is)
    // Note: Best-fit (Pass 2) runs later in the code flow. If best-fit successfully reorders items,
    // it will do a 'continue' which skips the rest of this iteration. However, chunking logic runs here
    // before best-fit. This is acceptable because:
    // 1. If chunking succeeds, the item is placed
    // 2. If chunking doesn't help (e.g., even 1 row doesn't fit), best-fit will run and may reorder
    // 3. The multi-pass nature means items may be processed multiple times until placed
    // For optimal flow, best-fit should ideally run before chunking, but the current structure works
    // because best-fit's continue will prevent further processing if it helps.
    if (drugsToProcess && drugsToProcess.length > 0 && !drugAdviceFitsAsIs) {
      // Check if there's already a chunk on this page
      const hasExistingChunkOnPage = currentOrderObject?.items?.some(item => item?.startsWith('drug_advice_chunk_'))
      
      if (hasExistingChunkOnPage) {
        // Move to next page - can't have multiple chunks on same page
        // CRITICAL: Mark all items on the current page as placed before moving to next page
        // This prevents them from being processed again
        if (Array.isArray(currentOrderObject?.items)) {
          currentOrderObject.items.forEach(itemId => {
            if (itemId && !itemId.startsWith('drug_advice_chunk_') && 
                itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo &&
                itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
              placedItems.add(itemId)
            }
          })
        }
        
        // Check if there are more items to process (not the last page)
        const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
        const hasMoreItems = currentItemIndex < bodyItemsInOrder.length - 1 || 
                             (remainingDrugsToProcess !== null && remainingDrugsToProcess.length > 0) || 
                             (remainingRows !== null && remainingRows.length > 0)
        
        // Only add continuedTo if there are more items to process (not the last page) and it's not already added
        if (hasMoreItems && !currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
          // Calculate remaining space before moving to next page
          // CRITICAL: Calculate total height by summing all items actually on this page
          let totalHeightOnPage = 0
          const itemSpacing = MEASUREMENTS.itemSpacing
          if (Array.isArray(currentOrderObject?.items)) {
            currentOrderObject.items.forEach(itemId => {
              if (itemId && 
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo &&
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
                const item = bodyItemsInOrder.find(i => i?.orderId === itemId)
                if (item?.dimension?.height) {
                  totalHeightOnPage += item.dimension.height + itemSpacing
                }
              }
            })
          }
          
          const remainingSpace = getRemainingPrescriptionBodyHeight(
            a4Height,
            headerHeightOnPage,
            footerHeightOnPage,
            totalHeightOnPage,
            dpi,
          )
          
          currentOrderObject = {
            ...currentOrderObject,
            items: [...currentOrderObject?.items, PRESCRIPTION_ITEM_ORDER_IDS.continuedTo],
            remainingSpace: remainingSpace, // Store remaining space for "continued on next page" box
          }
        }
        
        finalOrderObjects.push(currentOrderObject)
        currPage += 1
        totalHeightInPxAccomodatedByItems = 0
        
        // Recalculate heights for new page
        headerHeightOnPage = getHeaderHeightBasedOnPolicyAndPage(headerHeight, currPage, headerPolicy)
        footerHeightOnPage = getFooterHeightBasedOnPolicyAndPage(footerHeight, currPage, footerPolicy)
        remainingPrescriptionBodyHeight = getRemainingPrescriptionBodyHeight(a4Height, headerHeightOnPage, footerHeightOnPage, 0, dpi)
      }
      
      // Calculate how many rows can fit on current page
      const estimatedRowHeight = MEASUREMENTS.estimatedRowHeight
      const headerRowHeight = MEASUREMENTS.headerRowHeight
      const rowPadding = MEASUREMENTS.rowPadding
      const chunkBottomPadding = MEASUREMENTS.chunkBottomPadding
      
      // SIMPLIFIED: remainingPrescriptionBodyHeight already has safety margin built in
      // No additional margin needed - just use the available space directly
      const adjustedRemainingHeight = remainingPrescriptionBodyHeight
      
      // CRITICAL FIX: If we have actual dimension, calculate minimum space based on proportional chunk size
      // For non-table formats (like paragraphList), the actual height per row may be different
      const actualDimensionHeight = bodyItem?.dimension?.height
      const hasActualDimension = actualDimensionHeight && actualDimensionHeight > 0
      
      // If we have actual dimension, calculate average height per row from the actual measurement
      // This is more accurate for non-table formats
      const actualAverageRowHeight = hasActualDimension && drugsToProcess.length > 0
        ? actualDimensionHeight / drugsToProcess.length
        : estimatedRowHeight
      
      // Minimum space threshold: Only require space for 1 row (not the full chunk structure)
      // This allows us to fit chunks when there's reasonable space, even if not enough for a large chunk
      // Use actual row height if available for more accurate calculation
      // For paragraph list format, headerRowHeight may not apply (no table header), so reduce threshold
      const effectiveHeaderHeight = hasActualDimension ? 0 : headerRowHeight // No header for non-table formats
      // SIMPLIFIED: Just need space for one row + bottom padding + item spacing
      const minimumSpaceForOneRow = effectiveHeaderHeight + actualAverageRowHeight + chunkBottomPadding + MEASUREMENTS.itemSpacing
      
      // Only move to next page if we can't fit even 1 row
      // This is less conservative and allows better space utilization
      if (adjustedRemainingHeight < minimumSpaceForOneRow) {
        // Move entire chunk to next page - don't place any rows here
        const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
        // Check if there are actually remaining drugs or rows to process
        const hasMoreItems = currentItemIndex < bodyItemsInOrder.length - 1 ||
                             (remainingDrugsToProcess !== null && remainingDrugsToProcess.length > 0) ||
                             (remainingRows !== null && remainingRows.length > 0)
        
        if (hasMoreItems && !currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
          // Calculate remaining space before moving to next page
          // CRITICAL: Calculate total height by summing all items actually on this page
          let totalHeightOnPage = 0
          const itemSpacing = MEASUREMENTS.itemSpacing
          if (Array.isArray(currentOrderObject?.items)) {
            currentOrderObject.items.forEach(itemId => {
              if (itemId && 
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo &&
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
                const item = bodyItemsInOrder.find(i => i?.orderId === itemId)
                if (item?.dimension?.height) {
                  totalHeightOnPage += item.dimension.height + itemSpacing
                }
              }
            })
          }
          
          const remainingSpace = getRemainingPrescriptionBodyHeight(
            a4Height,
            headerHeightOnPage,
            footerHeightOnPage,
            totalHeightOnPage,
            dpi,
          )
          
          currentOrderObject = {
            ...currentOrderObject,
            items: [...currentOrderObject?.items, PRESCRIPTION_ITEM_ORDER_IDS.continuedTo],
            remainingSpace: remainingSpace,
          }
        }
        
        finalOrderObjects.push(currentOrderObject)
        currPage += 1
        totalHeightInPxAccomodatedByItems = 0
        
        // Recalculate heights for new page
        headerHeightOnPage = getHeaderHeightBasedOnPolicyAndPage(headerHeight, currPage, headerPolicy)
        footerHeightOnPage = getFooterHeightBasedOnPolicyAndPage(footerHeight, currPage, footerPolicy)
        
        // CRITICAL: For onLastPage and inLineAtEnd policies, check if footer should be on this page
        if ((footerPolicy === FOOTER_TYPES.onLastPage || footerPolicy === FOOTER_TYPES.inLineAtEnd) && footerHeight > 0) {
          const footerOnCurrentPage = currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
          const footerItem = bodyItemsInOrder.find(item => item?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
          
          if (footerItem) {
            const footerItemIndex = bodyItemsInOrder.indexOf(footerItem)
            const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
            const footerIsUpcoming = footerItemIndex > currentItemIndex
            
            if (footerOnCurrentPage || footerIsUpcoming) {
              footerHeightOnPage = footerHeight
            }
          }
        }
        
        remainingPrescriptionBodyHeight = getRemainingPrescriptionBodyHeight(a4Height, headerHeightOnPage, footerHeightOnPage, 0, dpi)
        
        // CRITICAL FIX: Use actual dimension to determine row sizing for non-table formats
        // For paragraph list format, calculate average row height from actual dimension
        const freshActualDimensionHeight = bodyItem?.dimension?.height
        const freshHasActualDimension = freshActualDimensionHeight && freshActualDimensionHeight > 0
        const freshActualAverageRowHeight = freshHasActualDimension && drugsToProcess.length > 0
          ? freshActualDimensionHeight / drugsToProcess.length
          : (isNonTableFormat ? Math.max(estimatedRowHeight, 80) : estimatedRowHeight)
        
        // For non-table formats (paragraphList/list), there's no table header - use 0
        // Also use 0 if we have actual dimensions (which already account for format)
        const freshEffectiveHeaderHeight = (freshHasActualDimension || isNonTableFormat) ? 0 : headerRowHeight
        const freshEffectiveRowHeight = freshActualAverageRowHeight + rowPadding
        
        // SIMPLIFIED: No additional safety margin - getRemainingPrescriptionBodyHeight already includes it
        // Just subtract header and padding for available content space
        const freshAdjustedRemainingHeight = remainingPrescriptionBodyHeight - freshEffectiveHeaderHeight - chunkBottomPadding
        const freshMaxRowsThatFit = Math.max(1, Math.ceil(freshAdjustedRemainingHeight / freshEffectiveRowHeight))
        
        // CRITICAL: Try to accommodate as many rows as possible on this new page
        // Step 1: Calculate how many rows fit in the initial available space
        let rowsToUse = Math.min(drugsToProcess.length, freshMaxRowsThatFit)
        
        // Step 2: If not all drugs fit initially, check if we can fit more rows
        // by calculating the space after placing the initial chunk
        if (rowsToUse < drugsToProcess.length) {
          // Calculate height of initial chunk - use effective heights for non-table formats
          const initialChunkHeight = freshEffectiveHeaderHeight + (rowsToUse * freshEffectiveRowHeight) + chunkBottomPadding
          
          // Calculate space left after placing the initial chunk
          // SIMPLIFIED: Just use item spacing, no additional safety margins
          const spaceAfterInitialChunk = remainingPrescriptionBodyHeight - initialChunkHeight - MEASUREMENTS.itemSpacing
          
          // Calculate how many additional rows can fit in the remaining space
          // We need header + rows + bottom padding, but since we're extending the same table,
          // we only need to account for the additional rows (no additional header)
          const availableForAdditionalRows = spaceAfterInitialChunk
          const maxAdditionalRowsThatFit = Math.max(0, Math.floor(availableForAdditionalRows / freshEffectiveRowHeight))
          
          // Try to fit as many additional rows as possible
          const remainingDrugs = drugsToProcess.length - rowsToUse
          const additionalRowsToUse = Math.min(remainingDrugs, maxAdditionalRowsThatFit)
          
          // If we can fit additional rows, add them to the chunk
          if (additionalRowsToUse > 0) {
            rowsToUse += additionalRowsToUse
          }
        }
        
        // Check if all drugs fit
        const allDrugsFit = rowsToUse >= drugsToProcess.length
        
        // Create chunk data with as many rows as we can fit
        const chunkData = drugsToProcess.slice(0, rowsToUse)
        
        // Only set remainingDrugsToProcess if there are actually remaining drugs that won't fit
        remainingDrugsToProcess = allDrugsFit ? null : drugsToProcess.slice(rowsToUse)
        
        // CRITICAL FIX: For proportional height calculation, use ORIGINAL total drugs and dimension
        const originalTotalDrugsForChunk = bodyItem?.fullDrugsAdvice?.length || drugsToProcess.length
        const originalDimensionForChunk = bodyItem?.dimension?.height || freshActualDimensionHeight
        
        // Calculate chunk height - use proportional height if actual dimension available
        const chunkHeight = freshHasActualDimension 
          ? (rowsToUse / originalTotalDrugsForChunk) * originalDimensionForChunk
          : freshEffectiveHeaderHeight + (rowsToUse * freshEffectiveRowHeight) + chunkBottomPadding
        
        const isContinuation = globalRowCounter > 0
        // CRITICAL FIX: Count chunks in finalOrderObjects. If we're continuing (globalRowCounter > 0)
        // but existingChunks is 0, it means the previous chunk exists but wasn't counted yet.
        // In this case, this should be chunk 1, not 0, to prevent duplicating chunk 0.
        const existingChunks = finalOrderObjects.filter(page => page?.chunkMetadata).length
        const chunkIndex = (isContinuation && existingChunks === 0) ? 1 : existingChunks
        
        const startRow = globalRowCounter + 1
        const endRow = globalRowCounter + rowsToUse
        globalRowCounter += rowsToUse
        
        // Calculate total number of drugs for title
        const totalDrugs = bodyItem?.fullDrugsAdvice?.length || drugsToProcess.length
        
        // Generate intelligent chunk title
        // Format: "Rx" for single chunk or last single item, "Rx (1-5 of 10)" for multiple chunks
        const generateChunkTitle = () => {
          // Case 1: All drugs fit in a single chunk starting from 1
          if (allDrugsFit && startRow === 1) {
            return 'Rx' // Single chunk with all items, no need for numbering
          }
          // Case 2: Last single item (e.g., 12 of 12) - no need for numbering
          if (startRow === endRow && endRow === totalDrugs) {
            return 'Rx'
          }
          return `Rx (${startRow}${endRow !== startRow ? `-${endRow}` : ''} of ${totalDrugs})`
        }
        
        const chunkItem = {
          ...bodyItem,
          orderId: `drug_advice_chunk_${chunkIndex}`,
          title: generateChunkTitle(),
          dimension: {
            ...bodyItem.dimension,
            height: chunkHeight,
          },
          chunkIndex,
          chunkData,
          startRow,
          endRow,
          totalDrugs,
          totalChunks: allDrugsFit ? 1 : (remainingDrugsToProcess && remainingDrugsToProcess.length > 0 ? chunkIndex + 2 : chunkIndex + 1),
        }
        
        const currentIndex = bodyItemsInOrder.indexOf(bodyItem)
        if (currentIndex >= 0 && isDrugAdviceItem) {
          bodyItemsInOrder.splice(currentIndex, 1, chunkItem)
        } else if (currentIndex >= 0 && isDrugAdviceChunk) {
          bodyItemsInOrder.splice(currentIndex, 1, chunkItem)
        }
        
        bodyItem = chunkItem
        currentItemHeight = chunkHeight
        
        // Create new page with continuedFrom and this chunk using getPageDetailsObject
        currentOrderObject = getPageDetailsObject(
          headerPolicy,
          footerPolicy,
          sidebarPolicy,
          currPage,
          [PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom, bodyItem?.orderId],
        )
        
        // Add chunk metadata
        currentOrderObject.chunkMetadata = {
          chunkIndex: bodyItem.chunkIndex,
          totalChunks: bodyItem.totalChunks,
          chunkData: bodyItem.chunkData,
          startRow: bodyItem.startRow,
          endRow: bodyItem.endRow,
        }
        
        const itemSpacing = MEASUREMENTS.itemSpacing
        totalHeightInPxAccomodatedByItems = chunkHeight + itemSpacing
        
        // Mark that chunk was moved to next page - skip normal chunk creation
        chunkMovedToNextPage = true
        // Set itemFits to true since we've already placed it on the new page
        itemFits = true
      }
      
      // Only create chunk in normal flow if it wasn't moved to next page
      if (!chunkMovedToNextPage) {
        // CRITICAL FIX: Use actual dimension to determine row sizing for non-table formats
        // For paragraph list format, calculate average row height from actual dimension
        const actualDimensionHeight = bodyItem?.dimension?.height
        const hasActualDimension = actualDimensionHeight && actualDimensionHeight > 0
        const actualAverageRowHeight = hasActualDimension && drugsToProcess.length > 0
          ? actualDimensionHeight / drugsToProcess.length
          : (isNonTableFormat ? Math.max(estimatedRowHeight, 80) : estimatedRowHeight)
        
        // For non-table formats (paragraphList/list), there's no table header - use 0
        // Also use 0 if we have actual dimensions (which already account for format)
        const effectiveHeaderHeight = (hasActualDimension || isNonTableFormat) ? 0 : headerRowHeight
        const effectiveRowHeight = actualAverageRowHeight + rowPadding
        
        // SIMPLIFIED: No additional safety margin - getRemainingPrescriptionBodyHeight already includes it
        // Calculate available space for rows - just subtract header and bottom padding
        const availableForRows = remainingPrescriptionBodyHeight - effectiveHeaderHeight - chunkBottomPadding
        // Use floor to be conservative and prevent overflow
        const maxRowsThatFit = Math.max(1, Math.floor(availableForRows / effectiveRowHeight))
        
        // CRITICAL: Try to accommodate as many rows as possible on this page
        // Step 1: Calculate how many rows fit in the initial available space
        let rowsToUse = Math.min(drugsToProcess.length, maxRowsThatFit)
        
        // Step 2: If not all drugs fit initially, check if we can fit more rows
        // by calculating the space after placing the initial chunk
        if (rowsToUse < drugsToProcess.length) {
          // Calculate height of initial chunk - use effective heights for non-table formats
          const initialChunkHeight = effectiveHeaderHeight + (rowsToUse * effectiveRowHeight) + chunkBottomPadding
          
          // SIMPLIFIED: Just use item spacing, no additional safety margins
          const spaceAfterInitialChunk = remainingPrescriptionBodyHeight - initialChunkHeight - MEASUREMENTS.itemSpacing
          
          // Calculate how many additional rows can fit in the remaining space
          // We need header + rows + bottom padding, but since we're extending the same table,
          // we only need to account for the additional rows (no additional header)
          const availableForAdditionalRows = spaceAfterInitialChunk
          const maxAdditionalRowsThatFit = Math.max(0, Math.floor(availableForAdditionalRows / effectiveRowHeight))
          
          // Try to fit as many additional rows as possible
          const remainingDrugs = drugsToProcess.length - rowsToUse
          const additionalRowsToUse = Math.min(remainingDrugs, maxAdditionalRowsThatFit)
          
          // If we can fit additional rows, add them to the chunk
          if (additionalRowsToUse > 0) {
            rowsToUse += additionalRowsToUse
          }
        }
        
        // Check if all drugs fit
        const allDrugsFit = rowsToUse >= drugsToProcess.length
        
        // Create chunk data with as many rows as we can fit
        const chunkData = drugsToProcess.slice(0, rowsToUse)
        
        // Only set remainingDrugsToProcess if there are actually remaining drugs that won't fit
        // If all drugs fit, set it to null to prevent unnecessary "continued" boxes
        remainingDrugsToProcess = allDrugsFit ? null : drugsToProcess.slice(rowsToUse)
        
        // Calculate chunk height - use effective heights for non-table formats
        // CRITICAL FIX: For proportional height calculation, we need to use the ORIGINAL total drugs
        // and ORIGINAL dimension, not the current remaining drugs. The average row height stays constant.
        // averageRowHeight = actualDimensionHeight / originalTotalDrugs
        // chunkHeight = rowsToUse * averageRowHeight
        // Which simplifies to: (rowsToUse / originalTotalDrugs) * actualDimensionHeight
        const originalTotalDrugs = bodyItem?.fullDrugsAdvice?.length || drugsToProcess.length
        const originalDimensionHeight = bodyItem?.dimension?.height || actualDimensionHeight
        
        const chunkHeight = hasActualDimension 
          ? (rowsToUse / originalTotalDrugs) * originalDimensionHeight
          : effectiveHeaderHeight + (rowsToUse * effectiveRowHeight) + chunkBottomPadding
        
        // Determine if this is a continuation (not the first chunk)
        const isContinuation = globalRowCounter > 0
        // CRITICAL FIX: Calculate chunk index based on ALL chunks created (including current page)
        // Count chunks in finalOrderObjects AND check if currentOrderObject has a chunk
        const existingChunks = finalOrderObjects.filter(page => page?.chunkMetadata).length
        const currentPageHasChunk = currentOrderObject?.chunkMetadata ? 1 : 0
        const chunkIndex = existingChunks + currentPageHasChunk
        
        // Calculate start and end row numbers (1-based)
        const startRow = globalRowCounter + 1
        const endRow = globalRowCounter + rowsToUse
        globalRowCounter += rowsToUse
        
        // Calculate total number of drugs for title
        const totalDrugs = bodyItem?.fullDrugsAdvice?.length || drugsToProcess.length
        
        // Generate intelligent chunk title
        // Format: "Rx" for single chunk or last single item, "Rx (1-5 of 10)" for multiple chunks
        const generateChunkTitle = () => {
          // Case 1: All drugs fit in a single chunk starting from 1
          if (allDrugsFit && startRow === 1) {
            return 'Rx' // Single chunk with all items, no need for numbering
          }
          // Case 2: Last single item (e.g., 12 of 12) - no need for numbering
          if (startRow === endRow && endRow === totalDrugs) {
            return 'Rx'
          }
          return `Rx (${startRow}${endRow !== startRow ? `-${endRow}` : ''} of ${totalDrugs})`
        }
        
        // Create chunk item
        const chunkItem = {
          ...bodyItem,
          orderId: `drug_advice_chunk_${chunkIndex}`,
          title: generateChunkTitle(),
          dimension: {
            ...bodyItem.dimension,
            height: chunkHeight,
          },
          chunkIndex,
          chunkData,
          startRow,
          endRow,
          totalDrugs,
          // If all drugs fit, this is the only chunk (totalChunks = 1)
          // Otherwise, estimate based on remaining drugs
          totalChunks: allDrugsFit ? 1 : (remainingDrugsToProcess && remainingDrugsToProcess.length > 0 ? chunkIndex + 2 : chunkIndex + 1),
        }
        
        // IMPORTANT: Replace the original drug_advice item in the array with the chunkItem
        // This prevents the original item from being processed again
        const currentIndex = bodyItemsInOrder.indexOf(bodyItem)
        if (currentIndex >= 0 && isDrugAdviceItem) {
          // Replace the original drug_advice item with the chunk item in the array
          bodyItemsInOrder[currentIndex] = chunkItem
          // Update bodyItem reference to point to the chunk item in the array
          bodyItem = chunkItem
        } else {
          // Fallback: just update the reference
          bodyItem = chunkItem
        }
        currentItemHeight = chunkHeight
      }
    } else if (isDrugAdviceChunk) {
      // This is an existing chunk (from previous processing), process normally
      // currentItemHeight already initialized above
    } else {
      // Regular item - currentItemHeight already initialized above
    }
    
    // For existing chunks (not dynamically created), check if they fit
    // If there's already a chunk on this page, move to next page
    const hasExistingChunkOnPage = isChunk && currentOrderObject?.items?.some(
      item => item?.startsWith('drug_advice_chunk_') || item?.startsWith('free_form_rich_text_chunk_')
    )
    
    let remainingRows = null
    let maxRowsThatFit = null
    
    // Only process existing chunks if they weren't already processed dynamically above
    if (isDrugAdviceChunk && bodyItem?.chunkData && Array.isArray(bodyItem.chunkData) && !hasExistingChunkOnPage && !drugsToProcess) {
      // Calculate actual available height for the drug advice chunk
      const estimatedRowHeight = 55
      const headerRowHeight = 50
      const rowPadding = 3
      const chunkBottomPadding = 25
      const safetyMargin = 15
      
      // CRITICAL FIX: The remainingPrescriptionBodyHeight calculation might not account for
      // items that are on the same page but were placed in the same iteration.
      // We need to be more conservative and account for items that come before drug_advice
      // in the order (observations, complaints, diagnosis) which are likely on the same page.
      
      // Find items that come before drug_advice in the original order
      const itemsBeforeDrugAdvice = bodyItemsInOrder.filter((item, index) => {
        const currentIndex = bodyItemsInOrder.indexOf(bodyItem)
        return index < currentIndex && 
               !item.orderId?.startsWith('drug_advice_chunk_') &&
               item.orderId !== 'drug_advice'
      })
      
      // Calculate height of items that are actually on the CURRENT page
      // Only count items that are already placed on this page, not items from previous pages
      let estimatedHeightOfPrecedingItems = 0
      itemsBeforeDrugAdvice.forEach(item => {
        // Only include items that are already on the current page
        if (currentOrderObject?.items?.includes(item.orderId)) {
          estimatedHeightOfPrecedingItems += (item?.dimension?.height || 0)
        }
        // Don't estimate items that aren't on the page yet - they might be on a previous page
      })
      
      // Adjust remaining height to be more conservative
      // Subtract estimated height of preceding items to get actual available space
      const adjustedRemainingHeight = remainingPrescriptionBodyHeight - estimatedHeightOfPrecedingItems
      
      // Available height for rows = adjusted remaining height - header - bottom padding - safety margin
      const availableForRows = adjustedRemainingHeight - safetyMargin - headerRowHeight - chunkBottomPadding
      
      // Calculate how many rows can actually fit
      maxRowsThatFit = Math.max(1, Math.floor(availableForRows / (estimatedRowHeight + rowPadding)))
      
      // Get the original chunk rows
      const originalChunkRows = bodyItem.chunkData.length
      
      // Use the smaller of: original chunk size or what fits
      const rowsToUse = Math.min(originalChunkRows, maxRowsThatFit)
      
      // Debug logging for chunk sizing (disabled to reduce console noise)
      // Uncomment if debugging chunking issues:
      // console.log('[Chunk Sizing]', {
      //   orderId: bodyItem.orderId,
      //   currPage,
      //   remainingHeight: Math.round(remainingPrescriptionBodyHeight),
      //   maxRowsThatFit,
      //   originalChunkRows,
      //   rowsToUse,
      // })
      
      // Recalculate chunk height based on actual rows that fit
      currentItemHeight = headerRowHeight + (rowsToUse * (estimatedRowHeight + rowPadding)) + chunkBottomPadding
      
      // ALWAYS resize the chunk to fit, even if it means splitting
      // If we can fit at least 1 row, resize and split
      // If we can't fit even 1 row, don't resize - move entire chunk to next page
      if (rowsToUse < originalChunkRows && rowsToUse >= 1) {
        // Store remaining rows before updating chunk data
        remainingRows = bodyItem.chunkData.slice(rowsToUse)
        
        // Update this chunk to only include rows that fit
        bodyItem.chunkData = bodyItem.chunkData.slice(0, rowsToUse)
        bodyItem.dimension = {
          ...bodyItem.dimension,
          height: currentItemHeight,
        }
        
        // Debug logging for chunk resizing (disabled to reduce console noise)
        // Uncomment if debugging chunking issues:
        // console.log('[Chunk Resized]', {
        //   orderId: bodyItem.orderId,
        //   page: currPage,
        //   rows: `${originalChunkRows}→${rowsToUse}`,
        //   remaining: remainingRows.length,
        // })
      } else if (rowsToUse === originalChunkRows && rowsToUse > maxRowsThatFit) {
        // Edge case: if calculation says we can fit but we actually can't
        // Force resize to maxRowsThatFit
        const forcedRowsToUse = maxRowsThatFit
        remainingRows = bodyItem.chunkData.slice(forcedRowsToUse)
        bodyItem.chunkData = bodyItem.chunkData.slice(0, forcedRowsToUse)
        currentItemHeight = headerRowHeight + (forcedRowsToUse * (estimatedRowHeight + rowPadding)) + chunkBottomPadding
        bodyItem.dimension = {
          ...bodyItem.dimension,
          height: currentItemHeight,
        }
        // Debug logging - commented out to reduce console noise
        // Uncomment if needed for debugging:
        // console.log('[Chunk Force Resized]', {
        //   orderId: bodyItem.orderId,
        //   originalRows: originalChunkRows,
        //   forcedRows: forcedRowsToUse,
        //   remainingRows: remainingRows.length,
        // })
      }
    }
    
    // CRITICAL: Check if item fits in remaining space
    // getRemainingPrescriptionBodyHeight already includes the base safety margin
    // The accumulated height (totalHeightInPxAccomodatedByItems) already includes itemSpacing after each placed item
    // So we DON'T need to subtract additional spacing here - that would be double-counting
    // Exception: For the FIRST item on a page, there's no spacing before it, but we should still
    // reserve some minimal space for visual separation from header
    const isFirstItemOnPage = currentOrderObject?.items?.filter(
      id => id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom
    )?.length === 0
    
    // For the first item, subtract itemSpacing to ensure minimal gap from header
    // For subsequent items, we also need to subtract itemSpacing because we need
    // space between the previous item and the current item
    // totalHeightInPxAccomodatedByItems includes spacing AFTER each item, but we need
    // spacing BEFORE the new item (between previous and new item)
    const adjustedRemainingHeight = remainingPrescriptionBodyHeight - MEASUREMENTS.itemSpacing
    
    // FREE-FORM RICH TEXT PIXEL-BASED WINDOWED CHUNKING
    // For free-form content that doesn't fit on the current page, we split it into
    // pixel-based "windows". Each window is a CSS-clipped view of the full HTML content,
    // showing only startY..startY+height pixels. This produces proper A4 pages on screen.
    if ((isFreeFormRichText && !freeFormFitsAsIs) || isFreeFormChunk) {
      const MIN_FF_CHUNK_HEIGHT = 40
      const isFirstFreeFormChunk = isFreeFormRichText && !freeFormFitsAsIs
      const chunkIndex = isFirstFreeFormChunk ? 0 : (bodyItem?.freeFormChunkData?.chunkIndex ?? freeFormChunkIdx)
      const startY = isFirstFreeFormChunk ? 0 : (bodyItem?.freeFormChunkData?.startY ?? freeFormNextStartY)
      const totalFreeForm = isFirstFreeFormChunk ? freeFormTotalPx : (freeFormTotalPx || bodyItem?.freeFormChunkData?.totalHeight || currentItemHeight)
      const remaining = isFirstFreeFormChunk ? freeFormTotalPx : (remainingFreeFormPx ?? bodyItem?.freeFormChunkData?.height ?? currentItemHeight)
      
      if (remaining > 0) {
        if (adjustedRemainingHeight < MIN_FF_CHUNK_HEIGHT) {
          // Not enough space on current page — push current page and start a new one
          if (!currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo) &&
              currentOrderObject?.items?.filter(id => id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom)?.length > 0) {
            currentOrderObject = {
              ...currentOrderObject,
              items: [...(currentOrderObject?.items || []), PRESCRIPTION_ITEM_ORDER_IDS.continuedTo],
            }
          }
          finalOrderObjects.push(currentOrderObject)
          currPage += 1
          totalHeightInPxAccomodatedByItems = 0
          headerHeightOnPage = getHeaderHeightBasedOnPolicyAndPage(headerHeight, currPage, headerPolicy)
          footerHeightOnPage = getFooterHeightBasedOnPolicyAndPage(footerHeight, currPage, footerPolicy)
          remainingPrescriptionBodyHeight = getRemainingPrescriptionBodyHeight(a4Height, headerHeightOnPage, footerHeightOnPage, 0, dpi)
          
          const freshAvailable = remainingPrescriptionBodyHeight - continuedDivHeight - MEASUREMENTS.itemSpacing
          const newChunkH = Math.min(remaining, Math.max(MIN_FF_CHUNK_HEIGHT, freshAvailable))
          
          const chunkItem = {
            orderId: `free_form_rich_text_chunk_${chunkIndex}`,
            title: bodyItem.title || 'Free-form Rich Text',
            dimension: { height: newChunkH },
            freeFormChunkData: { chunkIndex, startY, height: newChunkH, totalHeight: totalFreeForm },
          }
          const currentIndex = bodyItemsInOrder.indexOf(bodyItem)
          if (currentIndex >= 0) bodyItemsInOrder.splice(currentIndex, 1, chunkItem)
          
          const newRemaining = remaining - newChunkH
          remainingFreeFormPx = newRemaining > 0 ? newRemaining : null
          freeFormNextStartY = startY + newChunkH
          freeFormChunkIdx = chunkIndex + 1
          
          if (newRemaining > 0) {
            const nextPlaceholder = {
              orderId: `free_form_rich_text_chunk_${chunkIndex + 1}`,
              title: bodyItem.title || 'Free-form Rich Text',
              dimension: { height: newRemaining },
              freeFormChunkData: { chunkIndex: chunkIndex + 1, startY: freeFormNextStartY, height: newRemaining, totalHeight: totalFreeForm },
            }
            bodyItemsInOrder.splice((currentIndex >= 0 ? currentIndex : bodyItemsInOrder.length) + 1, 0, nextPlaceholder)
          }
          
          currentOrderObject = getPageDetailsObject(headerPolicy, footerPolicy, sidebarPolicy, currPage,
            [PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom, chunkItem.orderId])
          currentOrderObject.freeFormChunkMetadata = { ...chunkItem.freeFormChunkData }
          totalHeightInPxAccomodatedByItems = newChunkH + MEASUREMENTS.itemSpacing
          
          bodyItem = chunkItem
          currentItemHeight = newChunkH
          chunkMovedToNextPage = true
          skipBestFitThisIteration = true
          itemFits = true
        } else {
          // Has enough space — create chunk sized exactly to the available space
          const newChunkH = Math.min(remaining, adjustedRemainingHeight)
          
          const chunkItem = {
            orderId: `free_form_rich_text_chunk_${chunkIndex}`,
            title: bodyItem.title || 'Free-form Rich Text',
            dimension: { height: newChunkH },
            freeFormChunkData: { chunkIndex, startY, height: newChunkH, totalHeight: totalFreeForm },
          }
          const currentIndex = bodyItemsInOrder.indexOf(bodyItem)
          if (currentIndex >= 0) bodyItemsInOrder.splice(currentIndex, 1, chunkItem)
          
          const newRemaining = remaining - newChunkH
          remainingFreeFormPx = newRemaining > 0 ? newRemaining : null
          freeFormNextStartY = startY + newChunkH
          freeFormChunkIdx = chunkIndex + 1
          
          if (newRemaining > 0) {
            const nextPlaceholder = {
              orderId: `free_form_rich_text_chunk_${chunkIndex + 1}`,
              title: bodyItem.title || 'Free-form Rich Text',
              dimension: { height: newRemaining },
              freeFormChunkData: { chunkIndex: chunkIndex + 1, startY: freeFormNextStartY, height: newRemaining, totalHeight: totalFreeForm },
            }
            bodyItemsInOrder.splice((currentIndex >= 0 ? currentIndex : bodyItemsInOrder.length) + 1, 0, nextPlaceholder)
          }
          
          bodyItem = chunkItem
          currentItemHeight = newChunkH
          skipBestFitThisIteration = true
          // itemFits is calculated by normal flow below — will be true since newChunkH <= adjustedRemainingHeight
        }
      } else {
        itemFits = true // Nothing to render
      }
    }

    // CRITICAL: Prevent multiple chunks on the same page
    // Each page should only have ONE chunk. If there's already a chunk on this page,
    // move the current chunk to the next page immediately.
    // Note: hasExistingChunkOnPage was already checked above to prevent unnecessary resizing
    // Skip this block for free-form chunks — they are fully handled by the FF chunking block above
    if (isChunk && hasExistingChunkOnPage && !isFreeFormChunk) {
      // Debug logging (disabled to reduce console noise)
      // console.log('[Moving Chunk to Next Page - Already One on Current Page]', {
      //   orderId: bodyItem.orderId,
      //   chunkIndex: bodyItem.chunkIndex,
      //   fromPage: currPage,
      //   chunkDataLength: bodyItem.chunkData?.length,
      // })
      
      // IMPORTANT: When moving a chunk to the next page, don't resize it
      // It should be placed as-is on the new page
      // Reset any resizing that might have happened (though it shouldn't have, since we checked above)
      remainingRows = null // Clear any remaining rows - don't create a new chunk
      
      // Finish current page and move to next page
      // CRITICAL: Mark all items on the current page as placed before moving to next page
      if (Array.isArray(currentOrderObject?.items)) {
        currentOrderObject.items.forEach(itemId => {
          if (itemId && !itemId.startsWith('drug_advice_chunk_') && 
              itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo &&
              itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
            placedItems.add(itemId)
          }
        })
      }
      
      // Check if there are more items to process (not the last page)
      const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
      const hasMoreItems = currentItemIndex < bodyItemsInOrder.length - 1 || 
                           (remainingDrugsToProcess !== null && remainingDrugsToProcess.length > 0) || 
                           (remainingRows !== null && remainingRows.length > 0)
      
      // Only add continuedTo if there are more items to process (not the last page) and it's not already added
      if (hasMoreItems && !currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
        // Calculate remaining space before moving to next page
        // CRITICAL: When there's a chunk on the page, we need to account for ALL items on the page
        // including the chunk and any other items that were placed before it
        // Find all items on the current page and sum their heights
        let totalHeightOnPage = totalHeightInPxAccomodatedByItems
        
        // If there's a chunk on the page, make sure we're using its actual height
        const existingChunkItemId = currentOrderObject?.items?.find(item => item?.startsWith('drug_advice_chunk_'))
        if (existingChunkItemId) {
          // Find the chunk item in bodyItemsInOrder to get its dimension
          const existingChunkItem = bodyItemsInOrder.find(item => item?.orderId === existingChunkItemId)
          if (existingChunkItem?.dimension?.height) {
            // Check if chunk height is already in totalHeightInPxAccomodatedByItems
            // If not, add it. If yes, make sure we're using the correct value
            // Recalculate total by summing all item heights on the page
            totalHeightOnPage = 0
            const itemSpacing = MEASUREMENTS.itemSpacing
            currentOrderObject.items.forEach(itemId => {
              if (itemId && !itemId.startsWith('drug_advice_chunk_') && 
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo &&
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
                const item = bodyItemsInOrder.find(i => i?.orderId === itemId)
                if (item?.dimension?.height) {
                  totalHeightOnPage += item.dimension.height + itemSpacing
                }
              }
            })
            // Add the chunk height
            totalHeightOnPage += existingChunkItem.dimension.height + itemSpacing
          }
        }
        
        const remainingSpace = getRemainingPrescriptionBodyHeight(
          a4Height,
          headerHeightOnPage,
          footerHeightOnPage,
          totalHeightOnPage,
        )
        
        // Only add if not already present to prevent duplicates
        if (!currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
          currentOrderObject = {
            ...currentOrderObject,
            items: [...currentOrderObject?.items, PRESCRIPTION_ITEM_ORDER_IDS.continuedTo],
            remainingSpace: remainingSpace, // Store remaining space for "continued on next page" box
          }
        } else {
          // Update remainingSpace even if continuedTo already exists
          currentOrderObject = {
            ...currentOrderObject,
            remainingSpace: remainingSpace,
          }
        }
      }
      finalOrderObjects.push(currentOrderObject)
      currPage += 1
      totalHeightInPxAccomodatedByItems = 0 // Reset height for new page
      
      // Create new page with continuedFrom and this chunk
      // IMPORTANT: Only add the chunk to the new page, not items that were already on previous pages
      currentOrderObject = getPageDetailsObject(
        headerPolicy,
        footerPolicy,
        sidebarPolicy,
        currPage,
        [PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom, bodyItem?.orderId],
      )
      
      // Store chunk metadata for the new page
      if (bodyItem?.chunkIndex !== undefined) {
        let startRowForChunk = null
        let endRowForChunk = null
        if (bodyItem?.chunkData) {
          // Count total rows in all previous pages' chunks
          let totalRowsBefore = 0
          for (let i = 0; i < currPage - 1; i++) {
            const prevPageData = finalOrderObjects[i]
            if (prevPageData?.chunkMetadata?.chunkData) {
              totalRowsBefore += prevPageData.chunkMetadata.chunkData.length
            }
          }
          startRowForChunk = totalRowsBefore + 1
          endRowForChunk = startRowForChunk + bodyItem.chunkData.length - 1
        }
        
        currentOrderObject.chunkMetadata = {
          chunkIndex: bodyItem.chunkIndex,
          totalChunks: bodyItem.totalChunks,
          chunkData: bodyItem.chunkData, // Use original chunk data, not resized
          startRow: startRowForChunk,
          endRow: endRowForChunk,
        }
      }
      
      // Recalculate heights for the new page context (update outer let variables — no re-declaration)
      headerHeightOnPage = getHeaderHeightBasedOnPolicyAndPage(
        headerHeight,
        currPage,
        headerPolicy,
      )
      footerHeightOnPage = getFooterHeightBasedOnPolicyAndPage(
        footerHeight,
        currPage,
        footerPolicy,
      )
      
      // CRITICAL: For onLastPage and inLineAtEnd policies, check if footer should be on this page
      if ((footerPolicy === FOOTER_TYPES.onLastPage || footerPolicy === FOOTER_TYPES.inLineAtEnd) && footerHeight > 0) {
        // Check if footer item is already on current page
        const footerOnCurrentPage = currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
        
        // Check if footer item exists in the items list and is upcoming
        const footerItem = bodyItemsInOrder.find(item => item?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
        
        if (footerItem) {
          const footerItemIndex = bodyItemsInOrder.indexOf(footerItem)
          const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
          const footerIsUpcoming = footerItemIndex > currentItemIndex
          
          // Always account for footer height if:
          // 1. Footer is already on current page, OR
          // 2. Footer is an upcoming item (conservative approach to prevent overflow)
          if (footerOnCurrentPage || footerIsUpcoming) {
            footerHeightOnPage = footerHeight
          }
        }
      }
      
      // Recalculate remaining height for the new page (update outer let — no re-declaration)
      remainingPrescriptionBodyHeight = getRemainingPrescriptionBodyHeight(
        a4Height,
        headerHeightOnPage,
        footerHeightOnPage,
        totalHeightInPxAccomodatedByItems,
        dpi,
      )
      
      // Recalculate item height for the new page context (use original chunk data)
      currentItemHeight = getCurrentItemHeight(bodyItem)
      // SIMPLIFIED: No additional safety margin - base margin already in getRemainingPrescriptionBodyHeight
      const localAdjustedRemainingHeight = remainingPrescriptionBodyHeight - MEASUREMENTS.itemSpacing
      
      // Check if chunk fits on the new page (it should, since it's a pre-calculated chunk)
      itemFits = currentItemHeight <= localAdjustedRemainingHeight
      
      // Debug logging (disabled to reduce console noise)
      // console.log('[Chunk Moved to New Page]', {
      //   orderId: bodyItem.orderId,
      //   chunkIndex: bodyItem.chunkIndex,
      //   newPage: currPage,
      //   itemHeight: Math.round(currentItemHeight),
      //   remainingHeight: Math.round(remainingPrescriptionBodyHeight),
      //   localAdjustedRemainingHeight: Math.round(localAdjustedRemainingHeight),
      //   fits: itemFits,
      //   chunkDataLength: bodyItem.chunkData?.length,
      // })
      
      // Continue processing - if it fits, it will be placed below
      // If it doesn't fit, it will be handled in the else block
    }

    // Special handling for patient history notes - skip dimension-based fitting
    // HTML/RTF content cannot be accurately measured, so we allow it to flow freely
    
    // Only calculate itemFits if it wasn't already set in the chunk movement logic above
    if (itemFits === false) {
      if (isPatientHistoryNotes) {
        itemFits = false
      } else if (drugAdviceFitsAsIs) {
        // Drug advice fits as-is (calculated above)
        itemFits = true
      } else if (isFreeFormRichText && freeFormFitsAsIs) {
        // Free-form fits as-is (calculated above) — treat like normal item
        itemFits = currentItemHeight <= adjustedRemainingHeight + (colorMode === 'bw' ? 20 : 30)
      } else if (isChunk) {
        // Chunk was resized (split) - the resized part should fit
        if (remainingRows !== null) {
          itemFits = true
        } 
        // Chunk wasn't resized - check if it fits based on height or maxRowsThatFit
        else if (maxRowsThatFit !== null && maxRowsThatFit >= 1) {
          // Chunk calculation ran and determined it fits
          itemFits = currentItemHeight <= adjustedRemainingHeight
        }
        // Chunk calculation didn't run or maxRowsThatFit < 1 - check based on height
        else {
          itemFits = currentItemHeight <= adjustedRemainingHeight
        }
      } else {
        // For signature items, use a more lenient check
        // Signature is typically the last item, and spacing is less critical for final placement
        // In colored mode, measured height may include visual elements that don't consume full vertical space
        // Allow signature to fit if it's within reasonable bounds (up to 25px over remaining space)
        // isSignatureItem is already declared above
        if (isSignatureItem) {
          // Use remainingPrescriptionBodyHeight directly (no spacing subtraction) for signature
          // Allow more tolerance for signature since it's the final item and measured height may be inflated
          // In B&W mode, signature cards are simpler and may have less padding, so allow more tolerance
          // In colored mode, we also need higher tolerance due to visual elements and spacing
          const signatureTolerance = colorMode === 'bw' ? 45 : 60
          const signatureAvailableSpace = remainingPrescriptionBodyHeight + signatureTolerance
          itemFits = currentItemHeight <= signatureAvailableSpace
        } else {
          // For other items, also allow some tolerance since measured heights may be slightly inflated
          // This helps prevent items from being pushed to next page when there's visually sufficient space
          // Use smaller tolerance than signature since these items aren't the final item
          const itemTolerance = colorMode === 'bw' ? 20 : 30
          const itemAvailableSpace = adjustedRemainingHeight + itemTolerance
          itemFits = currentItemHeight <= itemAvailableSpace
        }
      }
    }
    
    // MULTI-PASS ALGORITHM: Pass 2 - Try best-fit reordering BEFORE chunking
    // This runs when item doesn't fit and best-fit is enabled
    // For drug advice, this runs if it doesn't fit as-is
    const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
    // Skip best-fit if this item was just inserted by best-fit (prevents infinite loops)
    const wasJustInserted = recentlyInsertedByBestFit.has(bodyItem?.orderId)
    // Run best-fit if: enabled, item doesn't fit (or we want to optimize), not patient history, not already a chunk, not just inserted
    // For drug advice, allow best-fit if it doesn't fit as-is
    // IMPORTANT: Disable best-fit when current item is free_form_rich_text — it would pull
    // next_visit_details / signature ahead of the rich text, breaking section ordering.
    const shouldTryBestFit = useBestFit && 
                            !isPatientHistoryNotes && 
                            !isChunk && 
                            !isFreeFormRichText &&
                            currentItemIndex < bodyItemsInOrder.length - 1 && 
                            !wasJustInserted &&
                            !skipBestFitThisIteration &&
                            (!itemFits || (isDrugAdviceItem && !drugAdviceFitsAsIs))
    
    if (shouldTryBestFit) {
      // Calculate remaining height after placing current item (if it fits)
      let remainingHeight = adjustedRemainingHeight
      if (itemFits) {
        // If current item fits, calculate remaining space after placing it
        remainingHeight = adjustedRemainingHeight - currentItemHeight - MEASUREMENTS.itemSpacing
      }
      
      // Only proceed if there's meaningful space left (at least bestFitMinimumSpace to fit a small item)
      // Lowered threshold to be more aggressive
      if (remainingHeight > MEASUREMENTS.bestFitMinimumSpace && currentItemIndex < bodyItemsInOrder.length - 1) {
        const fittingItemsToProcess = []
        
        // Look ahead and collect all items that can fit, sorted by height (smallest first)
        const candidateItems = []
        for (let i = currentItemIndex + 1; i < bodyItemsInOrder.length; i++) {
          const candidateItem = bodyItemsInOrder[i]
          
          // Skip already placed items
          const isChunkItem = candidateItem?.orderId?.startsWith('drug_advice_chunk_')
          const inPlacedItems = !isChunkItem && placedItems.has(candidateItem?.orderId)
          const inCurrentPage = !isChunkItem && currentOrderObject?.items?.includes(candidateItem?.orderId)
          if (inPlacedItems || inCurrentPage) {
            continue
          }
          
          // Skip chunks if page already has one
          const isCandidateChunk = candidateItem?.orderId?.startsWith('drug_advice_chunk_') || candidateItem?.orderId?.startsWith('free_form_rich_text_chunk_')
          const hasChunkOnPage = currentOrderObject?.items?.some(id => id?.startsWith('drug_advice_chunk_') || id?.startsWith('free_form_rich_text_chunk_'))
          if (isCandidateChunk && hasChunkOnPage) {
            continue
          }
          
          // Skip patient history notes and free-form rich text (they need special chunking handling)
          if (candidateItem?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes ||
              candidateItem?.orderId === 'free_form_rich_text') {
            continue
          }
          
          // Skip next_visit_details and signature if there are still unplaced free_form items
          // before this candidate — they must never jump ahead of the rich text.
          const isOrderSensitive = candidateItem?.orderId === 'next_visit_details' ||
                                   candidateItem?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.signature
          if (isOrderSensitive) {
            const hasUnplacedFreeFormBefore = bodyItemsInOrder.slice(0, i).some(prev =>
              (prev?.orderId === 'free_form_rich_text' ||
               prev?.orderId?.startsWith('free_form_rich_text_chunk_')) &&
              !placedItems.has(prev?.orderId) &&
              !currentOrderObject?.items?.includes(prev?.orderId)
            )
            if (hasUnplacedFreeFormBefore) continue
          }
          
          const candidateHeight = getCurrentItemHeight(candidateItem)
          if (candidateHeight > 0) {
            candidateItems.push({
              item: candidateItem,
              index: i,
              height: candidateHeight,
            })
          }
        }
        
        // Sort by height (smallest first) to maximize space utilization
        candidateItems.sort((a, b) => a.height - b.height)
        
        // Try to fit as many items as possible in remaining space
        // SIMPLIFIED: Just use item spacing, no additional safety margin (base margin already included)
        let tempRemainingHeight = remainingHeight
        for (const candidate of candidateItems) {
          const totalCandidateHeight = candidate.height + MEASUREMENTS.itemSpacing
          
          if (totalCandidateHeight <= tempRemainingHeight) {
            fittingItemsToProcess.push(candidate)
            tempRemainingHeight -= totalCandidateHeight
          }
        }
        
        // If we found fitting items, insert them before current item so they're processed first
        if (fittingItemsToProcess.length > 0) {
          // Sort by original index to maintain logical order
          fittingItemsToProcess.sort((a, b) => a.index - b.index)
          
          // Remove fitting items from their original positions (in reverse order to maintain indices)
          let adjustedCurrentIndex = currentItemIndex
          for (let i = fittingItemsToProcess.length - 1; i >= 0; i--) {
            const fittingItem = fittingItemsToProcess[i]
            const originalIndex = bodyItemsInOrder.indexOf(fittingItem.item)
            if (originalIndex !== -1) {
              bodyItemsInOrder.splice(originalIndex, 1)
              // Adjust currentItemIndex if items were removed before it
              if (originalIndex < adjustedCurrentIndex) {
                adjustedCurrentIndex--
              }
            }
          }
          
          // Insert fitting items before the current item
          for (let i = fittingItemsToProcess.length - 1; i >= 0; i--) {
            const fittingItem = fittingItemsToProcess[i]
            bodyItemsInOrder.splice(adjustedCurrentIndex, 0, fittingItem.item)
            // Mark as recently inserted to prevent best-fit from running on them immediately
            recentlyInsertedByBestFit.add(fittingItem.item?.orderId)
          }
          
          // Reset itemIndex to process from the first inserted item
          itemIndex = adjustedCurrentIndex
          
          // Debug log to verify algorithm is running (disabled to reduce console noise)
          // Uncomment if debugging best-fit issues:
          // console.log('[Best-Fit Algorithm] Reordering items:', {
          //   currentItem: bodyItem?.orderId,
          //   currentItemHeight: Math.round(currentItemHeight),
          //   fittingItemsCount: fittingItemsToProcess.length,
          //   fittingItems: fittingItemsToProcess.map(f => ({ id: f.item?.orderId, height: Math.round(f.height) })),
          //   remainingHeight: Math.round(remainingHeight),
          //   page: currPage,
          // })
          
          // Continue to process the inserted items (they'll be processed normally without best-fit)
          continue
        }
      }
    }
    
    
    // Increment index for next iteration (only if we didn't continue above)
    itemIndex++
    
    // Debug logging for chunks (disabled to reduce console noise)
    // Uncomment if debugging chunking issues:
    // if (isChunk) {
    //   console.log('[Item Fitting Check - Chunk]', {
    //     orderId: bodyItem.orderId,
    //     chunkIndex: bodyItem.chunkIndex,
    //     page: currPage,
    //     itemHeight: Math.round(currentItemHeight),
    //     remainingHeight: Math.round(remainingPrescriptionBodyHeight),
    //     adjustedRemainingHeight: Math.round(adjustedRemainingHeight),
    //     maxRowsThatFit,
    //     remainingRows: remainingRows?.length || null,
    //     fits: itemFits,
    //     willBePlaced: itemFits,
    //   })
    // }
    
    // SPECIAL HANDLING: Signature card (next_visit_details) should never be alone on a page
    // Only check if it would be alone AFTER confirming it actually fits
    // If it fits but would be alone, we'll handle it in the placement logic below
    // isSignatureItem is already declared above
    
    // Only place item if it fits with safety margin
    if (itemFits) {
      
      // Calculate startRow and endRow for chunk metadata
      let startRowForChunk = null
      let endRowForChunk = null
      if (isChunk && bodyItem?.chunkData) {
        // Use the startRow and endRow calculated during dynamic processing
        // If not available, calculate from previous pages
        if (bodyItem.startRow && bodyItem.endRow) {
          startRowForChunk = bodyItem.startRow
          endRowForChunk = bodyItem.endRow
        } else {
          // Fallback: Count total rows in all previous pages' chunks
          let totalRowsBefore = 0
          for (let i = 0; i < currPage - 1; i++) {
            const prevPageData = finalOrderObjects[i]
            if (prevPageData?.chunkMetadata?.chunkData) {
              totalRowsBefore += prevPageData.chunkMetadata.chunkData.length
            }
          }
          startRowForChunk = totalRowsBefore + 1
          endRowForChunk = startRowForChunk + bodyItem.chunkData.length - 1
        }
      }

      // CRITICAL: Double-check that this item isn't already on this page or a previous page
      const itemAlreadyOnThisPage = Array.isArray(currentOrderObject?.items) && currentOrderObject.items.includes(bodyItem?.orderId)
      // CRITICAL FIX: For chunks, check if a chunk with the same index already exists on a previous page OR current page
      // This prevents duplicate chunks with the same index
      const itemAlreadyOnPreviousPage = bodyItem?.orderId?.startsWith('drug_advice_chunk_')
        ? (finalOrderObjects.some(page => 
            page?.chunkMetadata?.chunkIndex === bodyItem?.chunkIndex
          ) || currentOrderObject?.chunkMetadata?.chunkIndex === bodyItem?.chunkIndex)
        : finalOrderObjects.some(page => 
            Array.isArray(page?.items) && page.items.includes(bodyItem?.orderId)
          )
      
      // SPECIAL HANDLING: If signature card fits but would be alone, bring previous item
      let signatureHandled = false
      if (isSignatureItem && !itemAlreadyOnThisPage && !itemAlreadyOnPreviousPage) {
        const currentPageContentItems = currentOrderObject?.items?.filter(
          id => id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo && 
                id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom
        )
        const wouldBeAlone = currentPageContentItems?.length === 0
        
        
        if (wouldBeAlone && currPage > 1) {
          // Try to bring last item from previous page
          const previousPage = finalOrderObjects[currPage - 2]
          if (previousPage?.items) {
            const previousPageContentItems = previousPage.items.filter(
              id => id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo && 
                    id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom &&
                    !id?.startsWith('drug_advice_chunk_')
            )
            
            if (previousPageContentItems.length > 0) {
              const lastItemId = previousPageContentItems[previousPageContentItems.length - 1]
              const lastItem = bodyItemsInOrder.find(i => i?.orderId === lastItemId)
              
              if (lastItem) {
                const lastItemHeight = lastItem?.dimension?.height || 0
                // When bringing previous item to empty page:
                // - Previous item becomes first item: needs spacing from header
                // - Signature is second item: needs spacing after previous item
                // Total: lastItemHeight + spacing + currentItemHeight + spacing
                const combinedHeight = lastItemHeight + currentItemHeight + (2 * MEASUREMENTS.itemSpacing)
                
                // Since page is empty (wouldBeAlone = true), adjustedRemainingHeight already subtracted
                // spacing for ONE item. For TWO items, we need to subtract one more spacing.
                // Available = adjustedRemainingHeight - MEASUREMENTS.itemSpacing
                // Or equivalently: remainingPrescriptionBodyHeight - 2 * MEASUREMENTS.itemSpacing
                const availableHeight = adjustedRemainingHeight - MEASUREMENTS.itemSpacing
                
                // Check if both fit on current page
                if (combinedHeight <= availableHeight) {
                  // Remove from previous page
                  previousPage.items = previousPage.items.filter(id => id !== lastItemId)
                  placedItems.delete(lastItemId)
                  
                  // Add both to current page
                  currentOrderObject = {
                    ...currentOrderObject,
                    items: [lastItemId, bodyItem?.orderId],
                  }
                  
                  // Update height
                  totalHeightInPxAccomodatedByItems = lastItemHeight + currentItemHeight + (2 * MEASUREMENTS.itemSpacing)
                  
                  // Mark both as placed
                  placedItems.add(lastItemId)
                  placedItems.add(bodyItem?.orderId)
                  
                  // Skip normal placement logic
                  signatureHandled = true
                  itemIndex += 1
                  continue
                }
              }
            }
          }
          // If we can't bring previous item, don't place signature alone - let it go to else block
          // by skipping the normal placement below
          signatureHandled = true
          itemFits = false
        }
      }
      
      // Skip normal placement if signature was handled and doesn't fit (will go to else block)
      if (!(signatureHandled && !itemFits)) {
        // Only add if not already present (chunks can appear on multiple pages, but regular items cannot)
      // CRITICAL FIX: For chunks, also check that we're not adding a duplicate chunk with the same index
      if (!itemAlreadyOnThisPage && !itemAlreadyOnPreviousPage) {
        // Clear from recentlyInsertedByBestFit since it's now being placed
        recentlyInsertedByBestFit.delete(bodyItem?.orderId)
        
        currentOrderObject = {
          ...currentOrderObject,
          items: [...currentOrderObject?.items, bodyItem?.orderId],
          // Store chunk metadata and data if this is a drug-advice chunk
          chunkMetadata: bodyItem?.chunkIndex !== undefined ? {
            chunkIndex: bodyItem.chunkIndex,
            totalChunks: bodyItem.totalChunks,
            chunkData: bodyItem.chunkData,
            startRow: startRowForChunk,
            endRow: endRowForChunk,
          } : currentOrderObject.chunkMetadata,
          // Store free-form chunk metadata for windowed CSS rendering
          freeFormChunkMetadata: bodyItem?.freeFormChunkData
            ? { ...bodyItem.freeFormChunkData }
            : currentOrderObject.freeFormChunkMetadata,
        }
        
        // Mark this item as placed to prevent duplication
        // IMPORTANT: Mark immediately after adding to page to prevent re-processing
        // Chunks (drug_advice and free_form_rich_text) are NOT added to placedItems — they can appear on multiple pages
        if (bodyItem?.orderId &&
            !bodyItem?.orderId?.startsWith('drug_advice_chunk_') &&
            !bodyItem?.orderId?.startsWith('free_form_rich_text_chunk_')) {
          placedItems.add(bodyItem.orderId)
        }
      } else {
        // Item already placed - skip adding it again but continue with height tracking
        // This prevents duplicates while maintaining proper height calculations
      }
      
      // If there are remaining drugs, we need to process them on the next page
      // This will be handled by continuing the loop - the remainingDrugsToProcess
      // will be picked up in the next iteration
      // Add item height plus gap to account for margins/spacing between items
      // Items in the actual rendering have margins/padding that aren't captured in dimension measurements
      // CRITICAL FIX: Don't double-count height for chunks that were already counted when moved to new page
      if (!chunkMovedToNextPage) {
        // Check if this is the last item on the page (no more items to process)
        // For the last item, we don't need spacing after it
        const isLastItemOnPage = itemIndex >= bodyItemsInOrder.length - 1
        
        // Use consistent item spacing from MEASUREMENTS (includes colored mode adjustment)
        // Only add spacing if this is NOT the last item on the page
        const heightBefore = totalHeightInPxAccomodatedByItems
        if (isLastItemOnPage) {
          totalHeightInPxAccomodatedByItems += currentItemHeight // No spacing after last item
        } else {
          totalHeightInPxAccomodatedByItems += currentItemHeight + MEASUREMENTS.itemSpacing
        }
        const heightAfter = totalHeightInPxAccomodatedByItems
      }
      // Note: chunkMovedToNextPage items already had their height added at line ~872
      
      // Debug: Log chunk data being stored (disabled to reduce console noise)
      // Uncomment if debugging chunking issues:
      // if (isChunk) {
      //   console.log('[Chunk Stored]', {
      //     orderId: bodyItem.orderId,
      //     page: currPage,
      //     chunkIdx: bodyItem.chunkIndex,
      //     rows: bodyItem.chunkData?.length,
      //     hasChunkData: !!bodyItem.chunkData,
      //   })
      // }
      
      // If there are remaining drugs, we need to process them on the next page
      // But first, we should process any items that come after drug_advice in the original order
      // on the current page if there's space, before creating the continuation chunk
      // This ensures proper ordering of sections
      if (remainingDrugsToProcess && remainingDrugsToProcess.length > 0) {
        // Create a continuation drug_advice item for the next iteration
        // IMPORTANT: Insert it right after the original drug_advice item position to maintain order
        // This ensures items that come after drug_advice in the original order are processed first
        const continuationItem = {
          ...bodyItem,
          orderId: 'drug_advice',
          fullDrugsAdvice: remainingDrugsToProcess,
        }
        
        // Insert the continuation item right after the chunk's current position
        // This ensures items that come after drug_advice in the original order are processed first
        // (they're already in the array after the original drug_advice position)
        // Then the continuation item will be processed, creating chunk 1
        const currentChunkIndex = bodyItemsInOrder.indexOf(bodyItem)
        const insertIndex = currentChunkIndex >= 0 ? currentChunkIndex + 1 : originalDrugAdviceIndex >= 0 ? originalDrugAdviceIndex + 1 : bodyItemsInOrder.length
        
        if (insertIndex >= 0 && insertIndex <= bodyItemsInOrder.length) {
          // Check if there's already a continuation item with the same remaining drugs
          const hasContinuation = bodyItemsInOrder.slice(insertIndex).some(
            item => item?.orderId === 'drug_advice' && 
                    item?.fullDrugsAdvice && 
                    item.fullDrugsAdvice.length === remainingDrugsToProcess.length &&
                    item.fullDrugsAdvice[0] === remainingDrugsToProcess[0]
          )
          if (!hasContinuation) {
            bodyItemsInOrder.splice(insertIndex, 0, continuationItem)
          }
        }
      }
      } // Close the if (!(signatureHandled && !itemFits)) block
    } else {
      // Special handling for patient history notes - allow free-flowing across pages
      if (bodyItem?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes) {
        // For patient history notes, we don't try to fit it - just place it and let CSS handle page breaks
        // Add it to current page if there's any space, otherwise start a new page
        if (currentOrderObject?.items?.length > 0) {
          // Current page has items, finish it and start new page for patient history notes
          // Only add if not already present to prevent duplicates
          if (!currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
            currentOrderObject = {
              ...currentOrderObject,
              items: [...currentOrderObject?.items, PRESCRIPTION_ITEM_ORDER_IDS.continuedTo],
            }
          }
          finalOrderObjects.push(currentOrderObject)
          currPage += 1
        }
        
        // Create a page for patient history notes - it will flow across multiple pages via CSS
        currentOrderObject = getPageDetailsObject(
          headerPolicy,
          footerPolicy,
          sidebarPolicy,
          currPage,
          [bodyItem?.orderId],
        )
        finalOrderObjects.push(currentOrderObject)
        // Don't increment currPage here - let CSS handle natural page breaks
        // The content will flow to additional pages automatically when printed
        totalHeightInPxAccomodatedByItems = 0 // Reset for patient history notes pages
      } else {
        // Item doesn't fit - move to next page
        
        // SPECIAL HANDLING: Signature card (next_visit_details) should never be alone on a page
        // If signature is being moved to a new page and current page has items,
        // check if it would be the only item on the new page
        // isSignatureItem is already declared above
        const currentPageHasItems = currentOrderObject?.items?.filter(
          id => id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo && 
                id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom
        )?.length > 0
        
        
        if (isSignatureItem && currentPageHasItems) {
          // Get the last item on the current page that could be moved with footer
          const currentPageContentItems = currentOrderObject?.items?.filter(
            id => id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo && 
                  id !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom &&
                  !id?.startsWith('drug_advice_chunk_') // Don't move drug advice chunks
          )
          
          if (currentPageContentItems?.length > 0) {
            const lastItemId = currentPageContentItems[currentPageContentItems.length - 1]
            const lastItem = bodyItemsInOrder.find(i => i?.orderId === lastItemId)
            
            // Check if we can bring the last item to the new page with footer
            // Calculate combined height of last item + footer
            const lastItemHeight = lastItem?.dimension?.height || 0
            const combinedHeight = lastItemHeight + currentItemHeight + MEASUREMENTS.itemSpacing
            
            // Get available height on a fresh page (with continuedFrom)
            // Note: The new page will have a "Continued from" div, so we need to account for continuedDivHeight
            const freshPageHeaderHeight = getHeaderHeightBasedOnPolicyAndPage(headerHeight, currPage + 1, headerPolicy)
            const freshPageFooterHeight = getFooterHeightBasedOnPolicyAndPage(footerHeight, currPage + 1, footerPolicy)
            const freshPageBodyHeight = getBodyHeight(a4Height, freshPageHeaderHeight, freshPageFooterHeight)
            const freshPageSafetyMargin = freshPageFooterHeight > 0 
              ? convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.baseSafetyMarginWithFooter, dpi)
              : convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.baseSafetyMarginWithoutFooter, dpi)
            // New page will have "Continued from" div, so subtract continuedDivHeight from available space
            const freshPageAvailable = freshPageBodyHeight - continuedDivHeight - freshPageSafetyMargin
            
            if (combinedHeight <= freshPageAvailable && lastItem) {
              // Remove last item from current page
              currentOrderObject = {
                ...currentOrderObject,
                items: currentOrderObject?.items?.filter(id => id !== lastItemId),
              }
              
              // Remove from placedItems so it can be placed on new page
              placedItems.delete(lastItemId)
              
              // Recalculate totalHeightInPxAccomodatedByItems by removing last item height
              totalHeightInPxAccomodatedByItems -= (lastItemHeight + MEASUREMENTS.itemSpacing)
              
              // Finish current page with continuedTo
              const currentItemIdx = bodyItemsInOrder.indexOf(bodyItem)
              const hasMoreItemsAfterSignature = currentItemIdx < bodyItemsInOrder.length - 1
              if (hasMoreItemsAfterSignature || true) { // Always add continuedTo when moving items
                if (!currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
                  currentOrderObject = {
                    ...currentOrderObject,
                    items: [...currentOrderObject?.items, PRESCRIPTION_ITEM_ORDER_IDS.continuedTo],
                  }
                }
              }
              
              finalOrderObjects.push(currentOrderObject)
              currPage += 1
              
              // Create new page with continuedFrom, moved item, AND signature
              currentOrderObject = getPageDetailsObject(
                headerPolicy,
                footerPolicy,
                sidebarPolicy,
                currPage,
                [PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom, lastItemId, bodyItem?.orderId],
              )
              
              // Mark both items as placed
              placedItems.add(lastItemId)
              placedItems.add(bodyItem?.orderId)
              
              // Update accumulated height for the new page
              totalHeightInPxAccomodatedByItems = lastItemHeight + currentItemHeight + (2 * MEASUREMENTS.itemSpacing) + continuedDivHeight
              
              // CRITICAL: Skip to next item - both lastItem and signature are now placed
              itemIndex += 1
              continue
            }
          }
        }
        
        // Check if there are more items to process (not the last page)
        const currentItemIndex = bodyItemsInOrder.indexOf(bodyItem)
        const hasMoreItems = currentItemIndex < bodyItemsInOrder.length - 1 || 
                             (remainingDrugsToProcess !== null && remainingDrugsToProcess.length > 0) || 
                             (remainingRows !== null && remainingRows.length > 0)
        
        // Only add continuedTo if there are more items to process (not the last page) and it's not already added
        if (hasMoreItems && !currentOrderObject?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
          // Calculate remaining space before moving to next page
          // CRITICAL: Calculate total height by summing all items actually on this page
          let totalHeightOnPage = 0
          const itemSpacing = MEASUREMENTS.itemSpacing
          if (Array.isArray(currentOrderObject?.items)) {
            currentOrderObject.items.forEach(itemId => {
              if (itemId && 
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo &&
                  itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
                const item = bodyItemsInOrder.find(i => i?.orderId === itemId)
                if (item?.dimension?.height) {
                  totalHeightOnPage += item.dimension.height + itemSpacing
                }
              }
            })
          }
          
          const remainingSpace = getRemainingPrescriptionBodyHeight(
            a4Height,
            headerHeightOnPage,
            footerHeightOnPage,
            totalHeightOnPage,
            dpi,
          )
          
          currentOrderObject = {
            ...currentOrderObject,
            items: [...currentOrderObject?.items, PRESCRIPTION_ITEM_ORDER_IDS.continuedTo],
            remainingSpace: remainingSpace, // Store remaining space for "continued on next page" box
          }
        }
        
        finalOrderObjects.push(currentOrderObject)
        currPage += 1
        // Add item height plus spacing buffer (consistent with placement logic)
        totalHeightInPxAccomodatedByItems = (bodyItem?.dimension?.height || 0) + continuedDivHeight + MEASUREMENTS.itemSpacing
        // Create new page - only add items that haven't been placed yet
        // Check if this item was already on a previous page
        const itemAlreadyOnPreviousPage = finalOrderObjects.some(page => 
          page?.items?.includes(bodyItem?.orderId) && 
          !bodyItem?.orderId?.startsWith('drug_advice_chunk_') &&
          !bodyItem?.orderId?.startsWith('free_form_rich_text_chunk_')
        )
        
        // Only add the item if it hasn't been placed on a previous page
        const itemsToAdd = itemAlreadyOnPreviousPage 
          ? [PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom]
          : [PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom, bodyItem?.orderId]
          
        currentOrderObject = getPageDetailsObject(
          headerPolicy,
          footerPolicy,
          sidebarPolicy,
          currPage,
          itemsToAdd,
        )
        
        // If we added the item, mark it as placed
        if (!itemAlreadyOnPreviousPage && bodyItem?.orderId &&
            !bodyItem?.orderId?.startsWith('drug_advice_chunk_') &&
            !bodyItem?.orderId?.startsWith('free_form_rich_text_chunk_')) {
          placedItems.add(bodyItem.orderId)
        }
        // Store free-form chunk metadata if this is a free-form chunk page
        if (bodyItem?.freeFormChunkData) {
          currentOrderObject.freeFormChunkMetadata = { ...bodyItem.freeFormChunkData }
        }
        // Store chunk metadata for continuation page - CRITICAL: include chunkData
        if (bodyItem?.chunkIndex !== undefined) {
          // Calculate startRow and endRow for chunk metadata on continuation page
          let startRowForChunk = null
          let endRowForChunk = null
          if (bodyItem?.chunkData) {
            // Count total rows in all previous pages' chunks
            let totalRowsBefore = 0
            for (let i = 0; i < currPage - 1; i++) {
              const prevPageData = finalOrderObjects[i]
              if (prevPageData?.chunkMetadata?.chunkData) {
                totalRowsBefore += prevPageData.chunkMetadata.chunkData.length
              }
            }
            startRowForChunk = totalRowsBefore + 1
            endRowForChunk = startRowForChunk + bodyItem.chunkData.length - 1
          }
          
          currentOrderObject.chunkMetadata = {
            chunkIndex: bodyItem.chunkIndex,
            totalChunks: bodyItem.totalChunks,
            chunkData: bodyItem.chunkData, // CRITICAL: Store the chunk data so it can be rendered
            startRow: startRowForChunk,
            endRow: endRowForChunk,
          }
        }
      }
    }
  }
  // todo: fooer on last page
  // todo: left and right sidebar space being left on alt pages. Should not happen

  if (!finalOrderObjects?.map((obj) => obj?.currPage)?.includes(currPage)) {
    finalOrderObjects.push(currentOrderObject)
  }

  // FINAL DEDUPLICATION PASS: Remove duplicate items from pages
  // This ensures no item appears on multiple pages (except chunks which can span pages)
  const seenItems = new Set()
  const deduplicatedOrderObjects = finalOrderObjects.map((page, pageIndex) => {
    if (!Array.isArray(page?.items)) {
      return page
    }
    
    const deduplicatedItems = []
    const pageItems = [...page.items] // Create a copy to avoid mutation issues
    const seenOnThisPage = new Set() // Track items seen on this specific page
    
    for (const itemId of pageItems) {
      // Allow chunks and continuation markers to appear on multiple pages
      // BUT prevent duplicates on the same page
      if (itemId?.startsWith('drug_advice_chunk_') || 
          itemId === PRESCRIPTION_ITEM_ORDER_IDS.continuedTo ||
          itemId === PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
        // Only add if not already on this page
        if (!seenOnThisPage.has(itemId)) {
          deduplicatedItems.push(itemId)
          seenOnThisPage.add(itemId)
        }
        continue
      }
      
      // For regular items, only add if not seen before
      if (itemId && !seenItems.has(itemId)) {
        deduplicatedItems.push(itemId)
        seenItems.add(itemId)
      }
      // If item was already seen, skip it (it's a duplicate)
    }
    
    return {
      ...page,
      items: deduplicatedItems,
    }
  })

  // FINAL PASS: Recalculate remainingSpace for each page based on final items
  // This ensures accurate remainingSpace calculation after all items are placed
  const finalOrderObjectsWithRecalculatedSpace = deduplicatedOrderObjects.map((page, pageIndex) => {
    // Only recalculate if this page has continuedTo
    if (!page?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo)) {
      return page
    }
    
    const pageNumber = pageIndex + 1
    const headerHeightOnPage = getHeaderHeightBasedOnPolicyAndPage(
      headerHeight,
      pageNumber,
      headerPolicy,
    )
    let footerHeightOnPage = getFooterHeightBasedOnPolicyAndPage(
      footerHeight,
      pageNumber,
      footerPolicy,
    )
    
    // CRITICAL: For onLastPage and inLineAtEnd policies, check if footer should be on this page
    if ((footerPolicy === FOOTER_TYPES.onLastPage || footerPolicy === FOOTER_TYPES.inLineAtEnd) && footerHeight > 0) {
      const footerOnCurrentPage = page?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
      const footerItem = bodyItemsInOrder.find(item => item?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
      
      if (footerItem) {
        // Check if footer is on this page
        if (footerOnCurrentPage) {
          footerHeightOnPage = footerHeight
        } else if (footerPolicy === FOOTER_TYPES.onLastPage && pageNumber === deduplicatedOrderObjects.length) {
          // Footer is on last page only
          footerHeightOnPage = footerHeight
        } else if (footerPolicy === FOOTER_TYPES.inLineAtEnd) {
          // For inLineAtEnd, check if footer appears on this or later pages
          const footerOnLaterPage = deduplicatedOrderObjects.slice(pageIndex + 1).some(p => 
            p?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.prescriptionFooter)
          )
          if (footerOnLaterPage) {
            // Conservative: account for footer if it's coming up
            footerHeightOnPage = footerHeight
          }
        }
      }
    }
    
    // Calculate total height by summing all items actually on this page
    let totalHeightOnPage = 0
    const itemSpacing = MEASUREMENTS.itemSpacing
    if (Array.isArray(page?.items)) {
      page.items.forEach(itemId => {
        if (itemId && 
            itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedTo &&
            itemId !== PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) {
          const item = bodyItemsInOrder.find(i => i?.orderId === itemId)
          if (item?.dimension?.height) {
            totalHeightOnPage += item.dimension.height + itemSpacing
          }
        }
      })
    }
    
    // Recalculate remaining space
    const remainingSpace = getRemainingPrescriptionBodyHeight(
      a4Height,
      headerHeightOnPage,
      footerHeightOnPage,
      totalHeightOnPage,
    )
    
    return {
      ...page,
      remainingSpace: remainingSpace,
    }
  })

  return finalOrderObjectsWithRecalculatedSpace
  } catch (error) {
    throw error;
  }
}

////////////////////PRIVATE///////////////////////////////
function getHeaderHeightBasedOnTypeAndPageIdx({ headerType, pageIdx, headerHeightSetByUser }) {
  switch (headerType) {
    case HEADER_TYPES.headerOnFirstPage:
      return pageIdx === 0 ? headerHeightSetByUser : 0
    case HEADER_TYPES.onAlternatePages:
      return pageIdx % 2 === 0 ? headerHeightSetByUser : 0
    // case HEADER_TYPES.blankHeader:
    // case HEADER_TYPES.onAllPages:
    //   return headerHeightSetByUser
    default:
      return headerHeightSetByUser
  }
}

function getPrescriptionBodyHeight({ pageHeight, headerHeight }) {
  return pageHeight - headerHeight
}

function getPageDetailsObject(
  headerPolicy,
  footerPolicy,
  sidebarPolicy,
  currPage,
  initialArray = [],
) {
  let obj = {
    items: [...initialArray],
    header: false,
    footer: false,
    leftSidebar: false,
    rightSidebar: false,
    page: currPage,
  }

  if (isHeaderRendered(headerPolicy, currPage)) {
    obj.header = true
  }
  if (isFooterRendered(footerPolicy, currPage)) {
    obj.footer = true
  }
  if (isLeftSidebarRendered(sidebarPolicy, currPage)) {
    obj.leftSidebar = true
  }
  if (isRightSidebarRendered(sidebarPolicy, currPage)) {
    obj.rightSidebar = true
  }
  return obj
}

function isHeaderRendered(headerPolicy, currPage) {
  if (headerPolicy === HEADER_TYPES.onFirstPage) {
    if (currPage !== 1) {
      return false
    }
  }
  if (headerPolicy === HEADER_TYPES.onAlternatePages) {
    if ((currPage - 1) % 2 !== 0) {
      return false
    }
  }
  if (headerPolicy === HEADER_TYPES.onFirstPage) {
    if (currPage > 1) {
      return false
    }
  }

  return true
}
function isFooterRendered(footerPolicy, currPage, isLastPage) {
  if (footerPolicy === FOOTER_TYPES.onAlternatePages) {
    if ((currPage - 1) % 2 !== 0) {
      return false
    }
  }
  if (footerPolicy === FOOTER_TYPES.onLastPage || footerPolicy === FOOTER_TYPES.inLineAtEnd) {
    return false
  }

  return true
}
function isLeftSidebarRendered(headerPolicy, currPage) {
  return true
}
function isRightSidebarRendered(headerPolicy, currPage) {
  return true
}

function getHeaderHeightBasedOnPolicyAndPage(headerHeight, currPage, headerPolicy) {
  if (isHeaderRendered(headerPolicy, currPage)) {
    return headerHeight ?? 0
  }
  return 0
}
function getFooterHeightBasedOnPolicyAndPage(footerHeight, currPage, footerPolicy) {
  if (isFooterRendered(footerPolicy, currPage)) {
    return footerHeight ?? 0
  }
  return 0
}

function getRemainingPrescriptionBodyHeight(
  a4Height,
  headerHeightOnPage,
  footerHeightOnPage,
  totalHeightInPxAccomodatedByItems,
  dpi = 96, // DPI for inch-to-pixel conversion (default 96 for web standard)
) {
  const bodyHeight = getBodyHeight(a4Height, headerHeightOnPage, footerHeightOnPage)
  
  // Add safety margin to prevent content from touching footer
  // This accounts for slight variations in text rendering between view and print
  // Increased margin for chunks to prevent overlap with footer
  // Convert from inches to pixels based on DPI
  const safetyMargin = footerHeightOnPage > 0 
    ? convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.baseSafetyMarginWithFooter, dpi)
    : convertInchesToPixels(PRESCRIPTION_MEASUREMENTS_IN_INCHES.baseSafetyMarginWithoutFooter, dpi)
  
  return bodyHeight - totalHeightInPxAccomodatedByItems - safetyMargin
}

function getCurrentItemHeight(bodyItem) {
  const baseHeight = bodyItem?.dimension?.height || 0
  
  // Special handling for patient history notes with unmeasured dimensions
  // Rich text content needs to render first before dimensions are accurate
  if (bodyItem?.orderId === PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes) {
    // If dimensions not measured yet (0 or undefined), return a minimal estimate
    // This ensures it gets placed on a page, and CSS will handle overflow
    if (!baseHeight || baseHeight === 0) {
      return 100 // Minimal placeholder - let CSS handle actual flow
    }
    // If we have actual dimensions, use them without buffer
    // CSS page-break properties will handle overflow naturally
    return baseHeight
  }

  // Free-form rich text: same treatment - CSS handles overflow for large content
  if (bodyItem?.orderId === 'free_form_rich_text') {
    if (!baseHeight || baseHeight === 0) {
      return 100 // Minimal placeholder - dimensions will be measured shortly
    }
    return baseHeight
  }
  
  // Free-form rich text chunks: return the pre-calculated chunk height directly
  if (bodyItem?.orderId?.startsWith('free_form_rich_text_chunk_')) {
    return bodyItem?.freeFormChunkData?.height || baseHeight || 100
  }
  
  // SIMPLIFIED: Minimal buffer for text-heavy items
  // The base safety margin in getRemainingPrescriptionBodyHeight is sufficient
  // Only add small buffer (5%) for text items that might expand slightly
  const textHeavyItems = ['instructions', 'advice', 'observations', 'health_complaints', 'diagnosis']
  const isTextHeavyItem = textHeavyItems.some(item => bodyItem?.orderId?.includes(item))
  
  // Reduced buffer from 10% to 5% for tighter fitting
  const buffer = isTextHeavyItem ? baseHeight * 0.05 : 0
  
  return baseHeight + buffer
}

function getBodyHeight(a4Height, headerHeight, footerHeightOnPage) {
  // Handle null/undefined values - default to 0
  const safeHeaderHeight = headerHeight ?? 0
  const safeFooterHeight = footerHeightOnPage ?? 0
  return a4Height - (safeHeaderHeight + safeFooterHeight)
}

function isItemFittingInRemainingPresciptionBody(
  remainingPrescriptionBodyHeight,
  currentItemHeight,
) {
  if (currentItemHeight > remainingPrescriptionBodyHeight) {
    return false
  }
  return true
}

// function getHeaderHeightBasedOnPolicyAndPage(headerHeight, currPage, headerPolicy) {

// }

function normalizeEmrSectionId(secId) {
  if (secId === 'therapyRecommended' || secId === 'suggestedTherapies') {
    return 'therapy_recommendation'
  }
  return secId
}

/**
 * Whether a section should be shown in the editor (EMR builder).
 * Uses: selected && !printOnly (if printOnly is true, section is print-only).
 */
export function checkShowInEditorForEmrSection(secId, emrSections) {
  const sectionId = normalizeEmrSectionId(secId)
  const section = emrSections?.find((s) => s?.id === sectionId)
  if (!section) return false
  if (!section?.selected) return false
  if (section?.printOnly === true) return false
  return true
}

/**
 * Whether a section should be shown in print/report view.
 * Uses: selected (for ordering), printable && !editorOnly.
 */
export function checkShowInPrintForEmrSection(secId, emrSections) {
  const sectionId = normalizeEmrSectionId(secId)
  const section = emrSections?.find((s) => s?.id === sectionId)
  if (!section) return false
  if (!section?.selected) return false
  if (!section?.printable) return false
  if (section?.editorOnly === true) return false
  return true
}

export function checkAndRenderBasedOnEmrSections(secId, emrSections, context = 'print') {
  let sectionId = secId

  if (sectionId === 'therapyRecommended' || sectionId === 'suggestedTherapies') {
    sectionId = 'therapy_recommendation'
  }

  const foundSection = emrSections?.find((section) => {
    if (section?.id === sectionId) {
      return true
    }
    return false
  })
  if (!foundSection) {
    return false
  }
  if (!foundSection?.selected) {
    return false
  }
  if (context === 'editor') {
    if (foundSection?.printOnly === true) return false
    return true
  }
  // print (default)
  if (!foundSection?.printable) {
    return false
  }
  if (foundSection?.editorOnly === true) {
    return false
  }
  return true
}

export function findEmrSectionBuSectionId(emrSections, sectionId, findOnUnselected) {
  return emrSections?.find((section) => {
    if (section?.id === sectionId) {
      return true
    }
    return false
  })
}

export function getA4Size() {
  const dpi = window.devicePixelRatio * 96 // Get approximate DPI
  return {
    width: Math.round((210 / 25.4) * dpi), // Convert mm to pixels
    height: Math.round((297 / 25.4) * dpi),
  }
}

// Generic pixel-to-inch conversion utilities
export const convertPixelsToInches = (pixels, dpi) => {
  return pixels / dpi
}

export const convertInchesToPixels = (inches, dpi) => {
  return inches * dpi
}

// A4 standard dimensions in inches
export const A4_DIMENSIONS_IN_INCHES = {
  width: 8.27,
  height: 11.69,
}

// Helper to get A4 dimensions in pixels for a given DPI
export const getA4DimensionsInPixels = (dpi) => {
  return {
    width: convertInchesToPixels(A4_DIMENSIONS_IN_INCHES.width, dpi),
    height: convertInchesToPixels(A4_DIMENSIONS_IN_INCHES.height, dpi),
  }
}

// Helper to format dimension display (pixels with inches)
export const formatDimensionWithInches = (pixels, dpi) => {
  const inches = convertPixelsToInches(pixels, dpi)
  return `${pixels.toFixed(2)}px (${inches.toFixed(2)}in)`
}


